import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import WelcomeEmailPanel from '../components/WelcomeEmailPanel';
import CampaignsPanelV2 from '../components/CampaignsPanelV2';
import MarketingStudio from '../components/MarketingStudio';
import PromotionsPanel from '../components/PromotionsPanel';
import EmailControlPanel from '../components/EmailControlPanel';
import AssistantsPanel from '../components/AssistantsPanel';
import PaypalSubscriptionsPanel from '../components/admin/PaypalSubscriptionsPanel';
import BulkMasteringPanel from '../components/admin/BulkMasteringPanel';
import PlaylistPromoAdminPanel from '../components/admin/PlaylistPromoAdminPanel';
import ManualSalesPanel from '../components/admin/ManualSalesPanel';
import ApprovalsPanel from '../components/ApprovalsPanel';
import ReplacementPanel from '../components/panels/ReplacementPanel';
import SupportPanel from '../components/panels/SupportPanel';
import ProductionPanel from '../components/panels/ProductionPanel';
import MasteringStaffPanel from '../components/panels/MasteringStaffPanel';
import UltimateRequestsPanel from '../components/panels/UltimateRequestsPanel';
import NewsPanel from '../components/panels/NewsPanel';
import GenreFilterBar from '../components/GenreFilterBar';
import { categoryForGenre } from '../utils/genreCategories';
import {
  LayoutDashboard, Music, Package, ShoppingCart, ShoppingBag, Users, Globe,
  Plus, Trash2, Edit3, DollarSign, TrendingUp, Eye, EyeOff, Activity,
  X, Save, RefreshCw, Search, Wallet, CheckCircle, XCircle, Clock,
  Upload, Star, Settings, FileText, Crown, MessageCircle, Send, CreditCard,
  Image, Layers, GripVertical, Tag, Mail, RotateCcw, Truck, HelpCircle, FileCheck, ChevronUp, ChevronDown, ChevronRight, Sparkles, Bell, ExternalLink, Loader2, UserPlus, ListMusic, LogOut, Newspaper, Play, Pause, Check, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Sidebar Tab ─────────────
const TabBtn = ({ icon: Icon, label, active, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${active ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
    <Icon className="w-5 h-5 flex-shrink-0" />
    <span className="hidden lg:inline">{label}</span>
  </button>
);

// ─── Stat Card ─────────────
const StatCard = ({ icon: Icon, label, value, sub, color = 'white' }) => (
  <div className="glass-card p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs text-white/40 uppercase tracking-wider">{label}</span>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color === 'orange' ? 'bg-[#E8720C]/20' : color === 'green' ? 'bg-green-500/20' : color === 'blue' ? 'bg-blue-500/20' : 'bg-white/10'}`}>
        <Icon className={`w-5 h-5 ${color === 'orange' ? 'text-[#E8720C]' : color === 'green' ? 'text-green-400' : color === 'blue' ? 'text-blue-400' : 'text-white/60'}`} />
      </div>
    </div>
    <p className="text-3xl font-bold">{value}</p>
    {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
  </div>
);

// ═══════════ MAIN ADMIN PAGE ═══════════
const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading, checkAuth } = useAuth();
  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [googleLinkError, setGoogleLinkError] = useState('');

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const linkGoogleAccount = useGoogleAuth({
    onSuccess: async () => { await checkAuth(); setGoogleLinkError(''); },
    onError: (msg) => setGoogleLinkError(msg),
  });
  const [geo, setGeo] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [packs, setPacks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminWallets, setAdminWallets] = useState([]);
  const [cryptoOrders, setCryptoOrders] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [leads, setLeads] = useState({ leads: [], stats: {} });
  const [leadFilter, setLeadFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [modal, setModal] = useState(null); // { type, data }
  const [walletForm, setWalletForm] = useState({ currency: 'usdt', address: '', network: '' });
  const [chatSessions, setChatSessions] = useState([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [settings, setSettings] = useState({ paypal: {}, whatsapp: {} });
  const [settingsForm, setSettingsForm] = useState({
    paypalClientId: '', paypalSecret: '',
    whatsappNumber: ''
  });
  const [emailSettings, setEmailSettings] = useState({ api_key: '', sender_email: '', configured: false, senders: [], email_types: [] });
  const [emailForm, setEmailForm] = useState({ api_key: '', sender_email: '' });
  const [emailSenders, setEmailSenders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [socialSettings, setSocialSettings] = useState({
    instagram_url: '', instagram_handle: '', tiktok_url: '', tiktok_handle: '',
    youtube_url: '', youtube_handle: '', spotify_url: '', spotify_handle: ''
  });
  const [adminArtists, setAdminArtists] = useState([]);
  const [artistModal, setArtistModal] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [bannerItems, setBannerItems] = useState([]);
  const [adminLang, setAdminLang] = useState(localStorage.getItem('admin_lang') || 'en');
  const [bannerModal, setBannerModal] = useState(null);
  const [bannerForm, setBannerForm] = useState({ title: '', subtitle: '', image_url: '', link: '', tag: '', active: true });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [faqItems, setFaqItems] = useState([]);
  const [faqModal, setFaqModal] = useState(null);
  const [licenseTemplate, setLicenseTemplate] = useState(null);
  const [aiSettings, setAiSettings] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userEditForm, setUserEditForm] = useState({ name: '', email: '' });
  const [userResetPw, setUserResetPw] = useState('');
  const [pwChangeForm, setPwChangeForm] = useState({ current: '', newPw: '', confirm: '' });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [needsAttentionPromos, setNeedsAttentionPromos] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [buyerHistory, setBuyerHistory] = useState(null);
  const [buyerHistoryLoading, setBuyerHistoryLoading] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploaded, setBulkUploaded] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [batchSettings, setBatchSettings] = useState({ bpm: '', price: '', genre: '', includes_playlist: false });
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [spModal, setSpModal] = useState(null);
  const [geoTab, setGeoTab] = useState('visitors');
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [salesSummary, setSalesSummary] = useState(null);
  const [emailPreviews, setEmailPreviews] = useState([]);
  const [activeEmailPreview, setActiveEmailPreview] = useState(null);
  const [trackCapacity, setTrackCapacity] = useState({ limit: 1000, visible: 0, remaining: 1000 });
  const [newCapacityLimit, setNewCapacityLimit] = useState('');
  const [employeeMetrics, setEmployeeMetrics] = useState([]);
  const [pendingProdApprovals, setPendingProdApprovals] = useState([]);
  const [prodApprovalLoading, setProdApprovalLoading] = useState(null);
  const [prodSelectAll, setProdSelectAll] = useState({});
  const [prodMetrics, setProdMetrics] = useState([]);
  const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [campaignSubTab, setCampaignSubTab] = useState('promotions');
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  // Track preview playback state (listen to verify genre)
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioPlayerRef = React.useRef(null);
  const [editingGenreId, setEditingGenreId] = useState(null);
  const [genreDraft, setGenreDraft] = useState('');
  // Genre category filter (balance del catálogo)
  const [genreCategory, setGenreCategory] = useState(null); // e.g. 'electronica'
  const [genreRaw, setGenreRaw] = useState(null);           // e.g. 'Afro House'

  const authHeaders = useCallback(() => ({ withCredentials: true }), []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadData();
  }, [user, navigate, authLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [a, g, t, p, o, u, w, c, m, sc, cs, ps, md, bn, dl, es, ar, ss, fq, lt, ai, ld] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics`, authHeaders()),
        axios.get(`${API_URL}/api/admin/geo`, authHeaders()),
        axios.get(`${API_URL}/api/admin/tracks`, authHeaders()),
        axios.get(`${API_URL}/api/admin/packs`, authHeaders()),
        axios.get(`${API_URL}/api/admin/orders`, authHeaders()),
        axios.get(`${API_URL}/api/admin/users`, authHeaders()),
        axios.get(`${API_URL}/api/admin/wallets`, authHeaders()),
        axios.get(`${API_URL}/api/admin/crypto-orders`, authHeaders()),
        axios.get(`${API_URL}/api/admin/memberships`, authHeaders()),
        axios.get(`${API_URL}/api/admin/content`, authHeaders()),
        axios.get(`${API_URL}/api/admin/chat-sessions`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/payment-settings`, authHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/admin/media`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/banners`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/deliveries`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/email-settings`, authHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/admin/artists`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/social-settings`, authHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/admin/faq`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/license-template`, authHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/ai-settings`, authHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/leads`, authHeaders()).catch(() => ({ data: { leads: [], stats: {} } })),
      ]);
      setAnalytics(a.data); setGeo(g.data); setTracks(t.data); setPacks(p.data);
      setOrders(o.data); setUsers(u.data); setAdminWallets(w.data);
      setCryptoOrders(c.data); setMemberships(m.data); setSiteContent(sc.data);
      setChatSessions(cs.data); setMediaItems(md.data || []); setBannerItems(bn.data || []);
      setDeliveries(dl.data || []);
      setAdminArtists(ar.data || []);
      if (ss.data && Object.keys(ss.data).length > 0) setSocialSettings(ss.data);
      setFaqItems(fq.data || []);
      if (lt.data) setLicenseTemplate(lt.data);
      if (ai.data) setAiSettings(ai.data);
      if (ld.data) setLeads(ld.data);
      if (es.data) {
        setEmailSettings(es.data);
        setEmailForm({ api_key: '', sender_email: es.data.sender_email || '' });
        setEmailSenders(es.data.senders || []);
      }
      const totalUnread = (cs.data || []).reduce((sum, s) => sum + (s.unread || 0), 0);
      setUnreadChatCount(totalUnread);
      // Load spotify playlists separately
      loadSpotifyPlaylists();
      if (ps.data) {
        setSettings(ps.data);
        setSettingsForm({
          paypalClientId: ps.data.paypal?.client_id || '',
          paypalSecret: '',
          whatsappNumber: ps.data.whatsapp?.number || ''
        });
      }
    } catch (err) { console.error('Admin load error:', err); }
    finally { setLoading(false); }
    // Load additional data in background
    try {
      const [capRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/track-capacity`, authHeaders()).catch(() => ({ data: { limit: 1000, visible: 0, remaining: 1000 } })),
        axios.get(`${API_URL}/api/admin/employee-metrics`, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setTrackCapacity(capRes.data);
      setNewCapacityLimit(String(capRes.data.limit));
      setEmployeeMetrics(empRes.data);
    } catch { }
  };

  // ─── Notification Polling ─────────────
  const loadNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications?limit=20`, authHeaders());
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) { /* ignore */ }
  }, []);

  const loadPromoAttention = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/playlist-promo/admin/needs-attention`, authHeaders());
      const c = r.data?.counts || {};
      // Critical: removals pending + paid waiting for activation
      setNeedsAttentionPromos((c.expired_pending || 0) + (c.paid_unactioned || 0));
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadNotifications();
    loadPromoAttention();
    const interval = setInterval(() => { loadNotifications(); loadPromoAttention(); }, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications, loadPromoAttention]);

  // ─── Sales Summary Loading ─────────────
  const loadSalesSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/sales-summary`, authHeaders());
      setSalesSummary(res.data);
    } catch (err) { console.error('Sales summary error:', err); }
  }, []);

  useEffect(() => {
    if (!loading && user?.role === 'admin') loadSalesSummary();
  }, [loading, user, loadSalesSummary]);

  // ─── Order Detail ─────────────
  const openOrderDetail = async (orderId) => {
    if (!orderId || orderId.length < 3) {
      console.warn('[OrderDetail] Invalid orderId:', orderId);
      return;
    }
    setOrderDetailLoading(true);
    setOrderDetail(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orders/${encodeURIComponent(orderId)}/detail`, authHeaders());
      setOrderDetail(res.data);
      setOrderDetailLoading(false);
    } catch (err) {
      console.error('[OrderDetail] Load failed:', err?.response?.data || err.message);
      setOrderDetailLoading(false);
      // Show inline error instead of leaving the modal in loading state
      setOrderDetail({
        _error: true,
        order_id: orderId,
        _error_msg: err?.response?.data?.detail || err.message || 'Could not load order',
      });
    }
  };

  const closeOrderDetail = () => { setOrderDetail(null); setOrderDetailLoading(false); };

  const handleVerifyOrder = async (orderId) => {
    try {
      await axios.post(`${API_URL}/api/admin/orders/${orderId}/verify`, {}, authHeaders());
      openOrderDetail(orderId);
    } catch (err) { alert('Verify failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleApproveCrypto = async (cryptoOrderId, action) => {
    try {
      await axios.put(`${API_URL}/api/admin/crypto-orders/${cryptoOrderId}/verify`, { status: action }, authHeaders());
      if (orderDetail) openOrderDetail(orderDetail.order_id);
      loadData();
    } catch (err) { alert('Action failed: ' + (err.response?.data?.detail || err.message)); }
  };

  // ─── Email Previews ─────────────
  const loadEmailPreviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-previews`, authHeaders());
      setEmailPreviews(res.data.previews || []);
      if (res.data.previews?.length) setActiveEmailPreview(res.data.previews[0].id);
    } catch (err) { console.error('Email preview error:', err); }
  };

  // ─── Notification Click Handler ─────────────
  const handleNotificationClick = async (n) => {
    if (!n.read) await markNotificationRead(n.id);
    setShowNotifications(false);

    // Production approval notifications → scroll to inline approvals section
    const nType = n.type || '';
    const nTitle = (n.title || '').toLowerCase();
    if (nType === 'price_approval' || nType === 'tracks_published' || nTitle.includes('aprobar precios') || nTitle.includes('tracks listos para aprobar')) {
      setTab('notifications');
      loadProdApprovals();
      setTimeout(() => {
        const el = document.getElementById('prod-approvals-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return;
    }

    setSelectedNotif(n);
    setTab('notifications');

    // For playlist notifications, fetch full buyer history
    if ((n.type === 'playlist_submission' || (n.title || '').toLowerCase().includes('playlist')) && n.data?.user_email) {
      setBuyerHistory(null);
      setBuyerHistoryLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/buyer-history?email=${encodeURIComponent(n.data.user_email)}`, authHeaders());
        setBuyerHistory(res.data);
      } catch (e) { console.error('Error fetching buyer history:', e); }
      setBuyerHistoryLoading(false);
    } else {
      setBuyerHistory(null);
    }
  };

  // ─── Production Approvals ─────────────
  const loadProdApprovals = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/production/pending-approvals`, authHeaders());
      setPendingProdApprovals(r.data);
    } catch {}
  }, []);

  const publishProdTracks = async (trackIds) => {
    setProdApprovalLoading('publish');
    try {
      await axios.post(`${API_URL}/api/production/publish-approved`, trackIds, authHeaders());
      loadProdApprovals();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setProdApprovalLoading(null);
  };

  const rejectProdTracks = async (trackIds) => {
    if (!window.confirm(`Rechazar ${trackIds.length} track(s)? Volveran a estado masterizado.`)) return;
    setProdApprovalLoading('reject');
    try {
      await axios.post(`${API_URL}/api/production/reject-pending`, { track_ids: trackIds }, authHeaders());
      loadProdApprovals();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setProdApprovalLoading(null);
  };

  // Load production approvals when on notifications tab
  useEffect(() => {
    if (tab === 'notifications') loadProdApprovals();
  }, [tab]);

  // Load production metrics on settings tab or overview
  useEffect(() => {
    if (tab === 'settings' || tab === 'overview') {
      (async () => {
        try {
          const r = await axios.get(`${API_URL}/api/production/assistant-metrics`, authHeaders());
          setProdMetrics(r.data);
        } catch {}
      })();
    }
  }, [tab]);

  // ─── CRUD Helpers ─────────────
  const apiCall = async (method, url, data) => {
    const config = authHeaders();
    if (method === 'post') return axios.post(`${API_URL}${url}`, data, config);
    if (method === 'put') return axios.put(`${API_URL}${url}`, data, config);
    if (method === 'delete') return axios.delete(`${API_URL}${url}`, config);
  };

  const handleSaveTrack = async (formData) => {
    if (formData.id) {
      await apiCall('put', `/api/admin/tracks/${formData.id}`, formData);
    } else {
      await apiCall('post', '/api/admin/tracks', formData);
    }
    setModal(null); loadData();
  };

  // --- Play preview to verify genre ---
  const togglePlayTrack = (track) => {
    const url = track.audio_preview_url || track.wav_file_url;
    if (!url) { alert('Este track no tiene audio disponible'); return; }
    if (playingTrackId === track.id) {
      try { audioPlayerRef.current?.pause(); } catch {}
      setPlayingTrackId(null);
      return;
    }
    if (!audioPlayerRef.current) {
      const el = new Audio();
      el.preload = 'auto';
      el.onended = () => setPlayingTrackId(null);
      el.onerror = () => { setPlayingTrackId(null); alert('Error reproduciendo el audio'); };
      audioPlayerRef.current = el;
    }
    try { audioPlayerRef.current.pause(); } catch {}
    audioPlayerRef.current.src = url;
    audioPlayerRef.current.currentTime = 0;
    audioPlayerRef.current.play().then(() => setPlayingTrackId(track.id))
      .catch(() => { setPlayingTrackId(null); alert('No se pudo reproducir'); });
  };

  // Cleanup audio on unmount
  useEffect(() => () => { try { audioPlayerRef.current?.pause(); } catch {} }, []);

  // --- Inline genre edit ---
  const startEditGenre = (track) => {
    setEditingGenreId(track.id);
    setGenreDraft(track.genre || '');
  };
  const cancelEditGenre = () => { setEditingGenreId(null); setGenreDraft(''); };
  const saveGenre = async (track) => {
    const nextGenre = (genreDraft || '').trim();
    if (!nextGenre || nextGenre === track.genre) { cancelEditGenre(); return; }
    try {
      // Preserve all track fields; only change genre
      await apiCall('put', `/api/admin/tracks/${track.id}`, {
        title: track.title,
        artist: track.artist || '',
        genre: nextGenre,
        vibe: track.vibe || '',
        bpm: Number(track.bpm || 0),
        key: track.key || '',
        duration: track.duration || '3:00',
        price: Number(track.price || 0),
        cover_image: track.cover_image || '',
        audio_preview_url: track.audio_preview_url || '',
        wav_file_url: track.wav_file_url || '',
        description: track.description || '',
        is_premium: !!track.is_premium,
        is_visible: track.is_visible !== false,
        includes_playlist: !!track.includes_playlist,
        license_type: track.license_type || 'basic',
        license_terms: track.license_terms || '',
      });
      cancelEditGenre();
      loadData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error actualizando género');
    }
  };

  const handleDeleteTrack = async (id) => {
    if (!window.confirm('Eliminar este track?')) return;
    await apiCall('delete', `/api/admin/tracks/${id}`);
    setSelectedTrackIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    loadData();
  };

  const handleBatchDeleteTracks = async () => {
    if (selectedTrackIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedTrackIds.size} track(s) seleccionado(s)?`)) return;
    setBatchDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/tracks/batch-delete`, { ids: Array.from(selectedTrackIds) }, authHeaders());
      setSelectedTrackIds(new Set());
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setBatchDeleting(false);
  };

  const toggleTrackSelect = (id) => {
    setSelectedTrackIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const toggleAllTracksSelect = () => {
    const filtered = tracks.filter(t => !searchTerm || t.title.toLowerCase().includes(searchTerm.toLowerCase()) || t.artist.toLowerCase().includes(searchTerm.toLowerCase()));
    if (selectedTrackIds.size === filtered.length) {
      setSelectedTrackIds(new Set());
    } else {
      setSelectedTrackIds(new Set(filtered.map(t => t.id)));
    }
  };

  const handleToggleVisibility = async (type, id, current) => {
    await apiCall('put', `/api/admin/${type}/${id}/visibility`, { is_visible: !current });
    loadData();
  };

  const handleTogglePremium = async (id, current) => {
    await apiCall('put', `/api/admin/tracks/${id}/premium`, { is_premium: !current });
    loadData();
  };

  const handleSavePack = async (formData) => {
    if (formData.id) {
      await apiCall('put', `/api/admin/packs/${formData.id}`, formData);
    } else {
      await apiCall('post', '/api/admin/packs', formData);
    }
    setModal(null); loadData();
  };

  const handleDeletePack = async (id) => {
    if (!window.confirm('Delete this pack?')) return;
    await apiCall('delete', `/api/admin/packs/${id}`);
    loadData();
  };

  const handleSaveWallet = async (e) => {
    e.preventDefault();
    if (!walletForm.address.trim()) return;
    await apiCall('post', '/api/admin/wallets', walletForm);
    setWalletForm({ currency: 'usdt', address: '', network: '' });
    loadData();
  };

  const handleVerifyCrypto = async (orderId, status) => {
    if (!window.confirm(`Mark as ${status}?`)) return;
    await apiCall('put', `/api/admin/crypto-orders/${orderId}/verify`, { status });
    loadData();
  };

  const handleSaveMembership = async (plan) => {
    await apiCall('put', `/api/admin/memberships/${plan.plan_id}`, plan);
    loadData();
  };

  const handleSaveContent = async (section, content) => {
    await apiCall('put', `/api/admin/content/${section}`, { content });
    loadData();
  };

  const handleUploadFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API_URL}/api/admin/upload/${type}`, formData, {
      ...authHeaders(),
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.url;
  };

  if (authLoading || !user || user.role !== 'admin') return null;

  // ═══════════ RENDER SECTIONS ═══════════

  const renderOverview = () => {
    if (!analytics) return <p className="text-white/40">Loading...</p>;
    const s = salesSummary || {};
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Dashboard Overview</h2>
          <button onClick={() => { loadData(); loadSalesSummary(); }} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {/* ── TOP STATS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div onClick={() => setTab('orders')} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard icon={DollarSign} label="Ingresos Totales" value={`$${(s.combined_revenue||analytics.revenue.total||0).toFixed(2)}`} sub={`PayPal $${(s.total_revenue||0).toFixed(2)} + Crypto $${(s.crypto_revenue||0).toFixed(2)} + Manual $${(s.manual_revenue||0).toFixed(2)}`} color="orange" />
          </div>
          <div onClick={() => setTab('users')} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard icon={Users} label="Usuarios" value={analytics.users.total} sub={`${analytics.users.new_today} nuevos hoy | ${analytics.users.online_now} online`} color="blue" />
          </div>
          <div onClick={() => setTab('tracks')} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard icon={Music} label="Catalogo" value={`${s.total_tracks || tracks.length} tracks`} sub={`${s.total_packs || packs.length} packs | ${s.visible_tracks || 0} visibles`} color="green" />
          </div>
          <div onClick={() => setTab('orders')} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard icon={ShoppingCart} label="Ordenes" value={orders.length} sub={`${s.paid_orders||0} pagadas | ${s.pending_orders||0} pendientes`} color="orange" />
          </div>
        </div>

        {/* ── VENTAS POR PERIODO ── */}
        <div className="glass-card p-6" data-testid="sales-periods">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 cursor-pointer" onClick={() => setTab('orders')}>
            <DollarSign className="w-5 h-5 text-[#E8720C]" /> Ventas por Periodo <ChevronRight className="w-4 h-4 text-white/20" />
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Hoy', revenue: s.today?.revenue || 0, count: s.today?.count || 0, color: 'text-[#E8720C]' },
              { label: 'Esta Semana', revenue: s.week?.revenue || 0, count: s.week?.count || 0, color: 'text-blue-400' },
              { label: 'Este Mes', revenue: s.month?.revenue || 0, count: s.month?.count || 0, color: 'text-green-400' },
              { label: 'Este Ano', revenue: s.year?.revenue || 0, count: s.year?.count || 0, color: 'text-purple-400' },
            ].map((p, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.05] transition-colors cursor-pointer" onClick={() => setTab('orders')}>
                <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">{p.label}</p>
                <p className={`text-2xl font-bold ${p.color}`}>${p.revenue.toFixed(2)}</p>
                <p className="text-xs text-white/40 mt-1">{p.count} orden{p.count !== 1 ? 'es' : ''}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── DINERO: PAYPAL vs CRYPTO vs MANUAL ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('orders')}>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-green-400">
              <CreditCard className="w-4 h-4" /> PayPal (Dolar)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-400">${(s.total_revenue||0).toFixed(2)}</p>
                <p className="text-[9px] text-white/30">Total Recaudado</p>
              </div>
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-400">{s.paid_orders||0}</p>
                <p className="text-[9px] text-white/30">Pagadas</p>
              </div>
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-yellow-400">{s.pending_orders||0}</p>
                <p className="text-[9px] text-white/30">Pendientes</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-white/30">
              <span>Promedio: <strong className="text-white/60">${(s.average_order_value||0).toFixed(2)}</strong></span>
            </div>
          </div>

          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('crypto')}>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-[#E8720C]">
              <DollarSign className="w-4 h-4" /> Crypto (USDT/BTC)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-[#E8720C]">${(s.crypto_revenue||0).toFixed(2)}</p>
                <p className="text-[9px] text-white/30">Aprobados</p>
              </div>
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-400">{s.crypto_approved||0}</p>
                <p className="text-[9px] text-white/30">Completados</p>
              </div>
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-yellow-400">{s.crypto_pending||0}</p>
                <p className="text-[9px] text-white/30">Pendientes</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 text-xs text-white/30">
              <span>Total ordenes crypto: <strong className="text-white/60">{s.crypto_orders||0}</strong></span>
            </div>
          </div>

          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('manual-sales')} data-testid="overview-manual-sales-card">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-blue-400">
              <BookOpen className="w-4 h-4" /> Ventas Manuales (Offline)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-blue-400">${(s.manual_revenue||0).toFixed(2)}</p>
                <p className="text-[9px] text-white/30">Recaudado Manual</p>
              </div>
              <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-blue-400">{s.manual_count||0}</p>
                <p className="text-[9px] text-white/30">Registros</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 text-xs text-white/30 flex items-center justify-between">
              <span>Historico y clientes fuera de la web</span>
              <ChevronRight className="w-3 h-3 text-white/30" />
            </div>
          </div>
        </div>

        {/* ── EMPLEADOS ── */}
        {employeeMetrics.length > 0 && (
          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('settings')}>
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" /> Rendimiento de Empleados <ChevronRight className="w-4 h-4 text-white/20" />
            </h3>
            <div className="space-y-3">
              {employeeMetrics.map(emp => {
                const pm = prodMetrics.find(m => m.email === emp.email);
                return (
                  <div key={emp.id} className="p-3 bg-white/[0.02] rounded-xl flex items-center gap-4">
                    <div className="w-9 h-9 rounded-full bg-purple-500/15 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-purple-400">{(emp.name||'?')[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{emp.name}</p>
                      <p className="text-[10px] text-white/30">{emp.email}</p>
                    </div>
                    <div className="flex gap-3 text-center shrink-0">
                      <div><p className="text-sm font-bold text-[#E8720C]">{emp.uploads.total}</p><p className="text-[8px] text-white/25">Subidos</p></div>
                      <div><p className="text-sm font-bold text-green-400">{emp.approvals.approved}</p><p className="text-[8px] text-white/25">Aprobados</p></div>
                      <div><p className="text-sm font-bold text-yellow-400">{emp.uploads.week}</p><p className="text-[8px] text-white/25">Semana</p></div>
                      {pm?.production && (
                        <>
                          <div><p className="text-sm font-bold text-blue-400">{pm.production.masterizados}</p><p className="text-[8px] text-white/25">Master.</p></div>
                          <div><p className="text-sm font-bold text-green-400">{pm.production.publicados}</p><p className="text-[8px] text-white/25">Public.</p></div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── QUICK ACCESS ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Tracks', val: s.total_tracks || tracks.length, icon: Music, tab: 'tracks', color: 'text-[#E8720C]' },
            { label: 'Packs', val: s.total_packs || packs.length, icon: Package, tab: 'packs', color: 'text-purple-400' },
            { label: 'Ofertas', val: '', icon: Tag, tab: 'campaigns', color: 'text-green-400', sub: 'Promociones' },
            { label: 'Notificaciones', val: unreadCount > 0 ? unreadCount : '', icon: Bell, tab: 'notifications', color: 'text-yellow-400' },
          ].map((item, i) => (
            <button key={i} onClick={() => setTab(item.tab)}
              className="glass-card p-4 text-left hover:bg-white/[0.05] transition-all hover:scale-[1.02] group"
              data-testid={`quick-${item.tab}`}>
              <div className="flex items-center gap-3">
                <item.icon className={`w-5 h-5 ${item.color} group-hover:scale-110 transition-transform`} />
                <div>
                  <p className="text-xs text-white/40">{item.label}</p>
                  {item.val !== '' && <p className={`text-lg font-bold ${item.color}`}>{item.val}</p>}
                  {item.sub && <p className="text-[9px] text-white/20">{item.sub}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* ── RECENT + TOP ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('orders')}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">Ordenes Recientes <ChevronRight className="w-4 h-4 text-white/20" /></h3>
            {analytics.recent_orders.length === 0 ? <p className="text-white/40 text-sm">Sin ordenes</p> : (
              <div className="space-y-3">
                {analytics.recent_orders.slice(0, 6).map((o, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div><p className="text-sm font-medium">{o.item_name}</p><p className="text-xs text-white/40">{new Date(o.created_at).toLocaleDateString()}</p></div>
                    <div className="text-right"><span className="gold-text font-medium">${o.amount}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${o.payment_status==='paid'?'bg-green-500/20 text-green-400':'bg-yellow-500/20 text-yellow-400'}`}>{o.payment_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('tracks')}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">Top Productos <ChevronRight className="w-4 h-4 text-white/20" /></h3>
            {analytics.top_products.length === 0 ? <p className="text-white/40 text-sm">Sin datos</p> : (
              <div className="space-y-3">
                {analytics.top_products.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-white/40">{p.count} ventas</p></div>
                    <span className="gold-text font-medium">${p.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── TRAFFIC ── */}
        <div className="glass-card p-6 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => setTab('geo')}>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-400" /> Trafico <ChevronRight className="w-4 h-4 text-white/20" />
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center"><p className="text-xl font-bold text-blue-400">{analytics.traffic.views_today}</p><p className="text-[9px] text-white/30">Vistas Hoy</p></div>
            <div className="text-center"><p className="text-xl font-bold text-purple-400">{analytics.traffic.views_this_week}</p><p className="text-[9px] text-white/30">Vistas Semana</p></div>
            <div className="text-center"><p className="text-xl font-bold text-green-400">{analytics.traffic.unique_visitors_today}</p><p className="text-[9px] text-white/30">Visitantes Unicos</p></div>
          </div>
        </div>
      </div>
    );
  };

  const renderTracks = () => {
    const filtered = tracks.filter(t => {
      // text search
      const matchesText = !searchTerm
        || t.title.toLowerCase().includes(searchTerm.toLowerCase())
        || (t.artist || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesText) return false;
      // category filter
      if (genreCategory && categoryForGenre(t.genre) !== genreCategory) return false;
      // sub-genre filter
      if (genreRaw && (t.genre || '').trim() !== genreRaw) return false;
      return true;
    });
    return (
      <div>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h2 className="text-2xl font-bold">
            Tracks ({filtered.length}{filtered.length !== tracks.length ? ` / ${tracks.length}` : ''})
          </h2>
          <div className="flex gap-3 items-center">
            {selectedTrackIds.size > 0 && (
              <button onClick={handleBatchDeleteTracks} disabled={batchDeleting}
                className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                data-testid="batch-delete-tracks-btn">
                {batchDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Eliminar {selectedTrackIds.size} seleccionado(s)
              </button>
            )}
            <label className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-xl text-sm text-white/70 flex items-center gap-2 cursor-pointer" data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4" /> Subida por Lote (MP3 + WAV)
              <input type="file" multiple accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden" onChange={e => setBulkFiles(Array.from(e.target.files))} />
            </label>
            <button onClick={() => setModal({ type: 'track', data: null })} className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-track-btn">
              <Plus className="w-4 h-4" /> Add Track
            </button>
          </div>
        </div>

        {/* Bulk Upload Zone */}
        {(bulkFiles.length > 0 || bulkUploaded.length > 0) && (
          <div className="glass-card p-6 mb-6 border border-[#E8720C]/20" data-testid="bulk-upload-zone">
            {bulkUploaded.length === 0 ? (
              <div>
                {/* Pair MP3+WAV files by name */}
                {(() => {
                  const mp3s = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.mp3'));
                  const wavs = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.wav'));
                  const getBase = (n) => n.replace(/\.[^/.]+$/, '').trim().toLowerCase();
                  const paired = [];
                  const unmatchedMp3 = [];
                  const unmatchedWav = [...wavs];
                  mp3s.forEach(mp3 => {
                    const base = getBase(mp3.name);
                    const wIdx = unmatchedWav.findIndex(w => getBase(w.name) === base);
                    if (wIdx >= 0) {
                      paired.push({ mp3, wav: unmatchedWav[wIdx], name: mp3.name.replace(/\.[^/.]+$/, '') });
                      unmatchedWav.splice(wIdx, 1);
                    } else {
                      unmatchedMp3.push(mp3);
                    }
                  });
                  const allOk = unmatchedMp3.length === 0 && unmatchedWav.length === 0 && paired.length > 0;
                  return (
                    <>
                      <h3 className="font-semibold mb-3">{bulkFiles.length} archivos seleccionados</h3>
                      
                      {/* Paired files */}
                      {paired.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] text-green-400 uppercase tracking-wider font-semibold mb-2">Parejas encontradas ({paired.length})</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {paired.map((p, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 bg-green-500/5 rounded-lg text-xs">
                                <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-[9px] font-bold">OK</span>
                                <span className="text-white/70">{p.name}</span>
                                <span className="text-white/30">MP3 ({(p.mp3.size / 1024 / 1024).toFixed(1)}MB)</span>
                                <span className="text-white/30">+ WAV ({(p.wav.size / 1024 / 1024).toFixed(1)}MB)</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Unmatched files */}
                      {(unmatchedMp3.length > 0 || unmatchedWav.length > 0) && (
                        <div className="mb-3 p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                          <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold mb-2">Archivos sin pareja</p>
                          {unmatchedMp3.map((f, i) => (
                            <div key={`m${i}`} className="text-xs text-red-400/70">MP3 sin WAV: {f.name}</div>
                          ))}
                          {unmatchedWav.map((f, i) => (
                            <div key={`w${i}`} className="text-xs text-red-400/70">WAV sin MP3: {f.name}</div>
                          ))}
                          <p className="text-[10px] text-white/40 mt-2">Cada track necesita un MP3 y un WAV con el mismo nombre.</p>
                        </div>
                      )}

                      {/* Batch Settings */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        <div>
                          <label className="block text-[10px] text-white/40 mb-1">BPM (todos)</label>
                          <input type="number" className="w-full input-glass text-xs py-2" placeholder="120"
                            value={batchSettings.bpm} onChange={e => setBatchSettings(p => ({ ...p, bpm: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-white/40 mb-1">Precio $ (todos)</label>
                          <input type="number" step="0.01" className="w-full input-glass text-xs py-2" placeholder="29.99"
                            value={batchSettings.price} onChange={e => setBatchSettings(p => ({ ...p, price: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-white/40 mb-1">Genero (todos)</label>
                          <input className="w-full input-glass text-xs py-2" placeholder="Reggaeton..."
                            value={batchSettings.genre} onChange={e => setBatchSettings(p => ({ ...p, genre: e.target.value }))} />
                        </div>
                      </div>
                      <button onClick={() => setBatchSettings(p => ({ ...p, includes_playlist: !p.includes_playlist }))}
                        className={`w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 mb-4 transition-all ${
                          batchSettings.includes_playlist ? 'bg-[#E8720C]/15 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10'
                        }`} data-testid="bulk-playlist-toggle">
                        {batchSettings.includes_playlist ? '+ Ecosistema de Playlists Activado' : 'Incluir Ecosistema de Playlists'}
                      </button>
                      <div className="flex gap-3">
                        <button onClick={handleBulkUpload} disabled={bulkUploading || !allOk}
                          className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2" data-testid="bulk-start-upload">
                          {bulkUploading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Subiendo...</> : `Subir ${paired.length} Tracks`}
                        </button>
                        <button onClick={() => setBulkFiles([])} className="px-5 py-2.5 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
                        {!allOk && <p className="text-xs text-red-400 self-center">Corrige los archivos sin pareja antes de subir</p>}
                      </div>
                      {/* Upload Progress Bar */}
                      {bulkUploading && bulkProgress.total > 0 && (
                        <div className="mt-4 p-4 bg-white/[0.03] border border-[#E8720C]/20 rounded-xl" data-testid="bulk-progress">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-white/60 font-medium">Subiendo archivos a S3...</span>
                            <span className="text-sm font-bold text-[#E8720C]">{bulkProgress.current}/{bulkProgress.total}</span>
                          </div>
                          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden mb-2">
                            <div className="h-full bg-[#E8720C] rounded-full transition-all duration-500" style={{ width: `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%` }} />
                          </div>
                          <p className="text-[10px] text-white/30">{Math.round((bulkProgress.current / bulkProgress.total) * 100)}% completado — {bulkProgress.total - bulkProgress.current} archivos restantes</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">{bulkUploaded.length} tracks listos — Edita los datos</h3>
                  <div className="flex gap-3">
                    <button onClick={applyBatchSettings} className="px-3 py-1.5 bg-white/10 rounded-lg text-xs text-white/60 hover:bg-white/15">
                      Aplicar Config a Todos
                    </button>
                    <button onClick={handleBulkCreate} disabled={bulkCreating} className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2" data-testid="bulk-create-tracks">
                      {bulkCreating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Creando...</> : `Crear ${bulkUploaded.length} Tracks`}
                    </button>
                    <button onClick={() => setBulkUploaded([])} className="px-4 py-2 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
                  </div>
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-7 gap-2 px-3 text-[10px] text-white/40 font-medium uppercase tracking-wider">
                    <span>Archivo</span><span>Titulo</span><span>Artista</span><span>Precio</span><span>Genero</span><span>BPM</span><span>Archivos</span>
                  </div>
                  {bulkUploaded.map((t, idx) => (
                    <div key={idx} className="grid grid-cols-7 gap-2 p-3 bg-white/[0.03] rounded-xl items-center" data-testid={`bulk-track-${idx}`}>
                      <div className="text-[10px] text-white/50 truncate" title={t.original_filename || t.filename}>{t.original_filename || t.filename}</div>
                      <input value={t.title} onChange={e => updateBulkTrack(idx, 'title', e.target.value)} placeholder="Titulo"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                      <input value={t.artist} onChange={e => updateBulkTrack(idx, 'artist', e.target.value)} placeholder="Artista"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                      <input type="number" value={t.price} onChange={e => updateBulkTrack(idx, 'price', e.target.value)} placeholder="29.99"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                      <input value={t.genre} onChange={e => updateBulkTrack(idx, 'genre', e.target.value)} placeholder="Genero"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                      <input type="number" value={t.bpm} onChange={e => updateBulkTrack(idx, 'bpm', e.target.value)} placeholder="BPM"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                      <div className="flex items-center gap-1">
                        {t.url && <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[9px] font-bold">MP3</span>}
                        {t.wav_url && <span className="px-1.5 py-0.5 bg-blue-500/15 text-blue-400 rounded text-[9px] font-bold">WAV</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <GenreFilterBar
          tracks={tracks}
          selectedCategory={genreCategory}
          selectedGenre={genreRaw}
          onSelectCategory={setGenreCategory}
          onSelectGenre={setGenreRaw}
        />
        <div className="glass-card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input className="w-full input-glass pl-11" placeholder="Search tracks..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="admin-track-search" />
          </div>
        </div>
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/40 border-b border-white/10">
                <th className="p-4 w-10"><input type="checkbox" checked={selectedTrackIds.size > 0 && selectedTrackIds.size === filtered.length} onChange={toggleAllTracksSelect} className="rounded border-white/20" data-testid="select-all-tracks" /></th>
                <th className="p-4">Title</th><th className="p-4">Artist</th><th className="p-4">Genre</th><th className="p-4">Price</th><th className="p-4 text-center">Premium</th><th className="p-4 text-center">Visible</th><th className="p-4 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(track => (
                  <tr key={track.id} className={`border-b border-white/5 hover:bg-white/5 ${!track.is_visible ? 'opacity-40' : ''} ${selectedTrackIds.has(track.id) ? 'bg-[#E8720C]/5' : ''}`} data-testid={`admin-track-row-${track.id}`}>
                    <td className="p-4"><input type="checkbox" checked={selectedTrackIds.has(track.id)} onChange={() => toggleTrackSelect(track.id)} className="rounded border-white/20" /></td>
                    <td className="p-4 font-medium">
                      {track.title}
                      {track.production_track_id && <span className="ml-2 px-1.5 py-0.5 bg-[#E8720C]/10 text-[#E8720C] rounded text-[8px] font-bold">PRODUCCION</span>}
                    </td>
                    <td className="p-4 text-white/60">{track.artist}</td>
                    <td className="p-4">
                      {editingGenreId === track.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            value={genreDraft}
                            onChange={e => setGenreDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveGenre(track); if (e.key === 'Escape') cancelEditGenre(); }}
                            list={`admin-genre-opts-${track.id}`}
                            className="px-2 py-1 bg-white/5 border border-[#E8720C]/40 rounded-lg text-xs text-white w-32 focus:outline-none focus:border-[#E8720C]"
                            placeholder="Género"
                            data-testid={`genre-input-${track.id}`}
                          />
                          <datalist id={`admin-genre-opts-${track.id}`}>
                            <option value="Afro House" />
                            <option value="Reggaeton" />
                            <option value="Dembow" />
                            <option value="Trap" />
                            <option value="Trap Latino" />
                            <option value="Hip-Hop" />
                            <option value="Drill" />
                            <option value="R&B" />
                            <option value="Electronic" />
                            <option value="House" />
                            <option value="Tech House" />
                            <option value="Deep House" />
                            <option value="Salsa" />
                            <option value="Bachata" />
                            <option value="Merengue" />
                            <option value="Regional Mexicano" />
                            <option value="Corridos Tumbados" />
                            <option value="Banda" />
                            <option value="Norteño" />
                            <option value="Baladas" />
                            <option value="Música Cristiana" />
                            <option value="Gospel" />
                            <option value="Pop Latino" />
                            <option value="Pop" />
                            <option value="Amapiano" />
                            <option value="Afrobeats" />
                            <option value="Dancehall" />
                            <option value="Cumbia" />
                            <option value="Guaracha" />
                            <option value="Lo-Fi" />
                            <option value="Beats Sin Letra" />
                            <option value="Instrumental" />
                          </datalist>
                          <button onClick={() => saveGenre(track)} className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid={`genre-save-${track.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={cancelEditGenre} className="p-1.5 rounded-lg bg-white/5 text-white/50 hover:bg-white/10" data-testid={`genre-cancel-${track.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditGenre(track)}
                          className="px-2 py-1 rounded-full text-xs bg-white/10 hover:bg-[#E8720C]/20 hover:text-[#E8720C] transition-colors"
                          title="Clic para cambiar género"
                          data-testid={`genre-edit-btn-${track.id}`}
                        >
                          {track.genre || '—'}
                        </button>
                      )}
                    </td>
                    <td className="p-4 gold-text font-medium">${track.price}</td>
                    <td className="p-4 text-center">
                      <button onClick={() => handleTogglePremium(track.id, track.is_premium)} className={`p-1.5 rounded-lg transition-colors ${track.is_premium ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-white/5 text-white/30'}`} data-testid={`premium-toggle-${track.id}`}>
                        <Crown className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <button onClick={() => handleToggleVisibility('tracks', track.id, track.is_visible)} className={`p-1.5 rounded-lg transition-colors ${track.is_visible ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid={`visibility-toggle-${track.id}`}>
                        {track.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => togglePlayTrack(track)}
                          className={`p-2 rounded-lg transition-colors ${playingTrackId === track.id ? 'bg-[#E8720C]/30 text-[#E8720C]' : 'hover:bg-[#E8720C]/15 text-white/60 hover:text-[#E8720C]'}`}
                          title={playingTrackId === track.id ? 'Pausar' : 'Escuchar preview'}
                          data-testid={`play-track-${track.id}`}
                        >
                          {playingTrackId === track.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setModal({ type: 'track', data: track })} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`edit-track-${track.id}`}><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteTrack(track.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400" data-testid={`delete-track-${track.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <p className="text-center text-white/40 py-8">No tracks found</p>}
        </div>
      </div>
    );
  };

  const renderPacks = () => (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="text-2xl font-bold">Packs ({packs.length})</h2>
        <button onClick={() => setModal({ type: 'pack', data: null })} className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-pack-btn">
          <Plus className="w-4 h-4" /> Add Pack
        </button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Name</th><th className="p-4">Genre</th><th className="p-4">Tracks</th><th className="p-4">Price</th><th className="p-4 text-center">Visible</th><th className="p-4 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {packs.map(pack => (
                <tr key={pack.id} className={`border-b border-white/5 hover:bg-white/5 ${!pack.is_visible ? 'opacity-40' : ''}`} data-testid={`admin-pack-row-${pack.id}`}>
                  <td className="p-4 font-medium">{pack.name} {pack.is_ultimate && <span className="text-xs text-[#E8720C]">(Ultimate)</span>}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-white/10">{pack.genre}</span></td>
                  <td className="p-4 text-white/60">{pack.track_count}</td>
                  <td className="p-4 gold-text font-medium">${pack.price}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => handleToggleVisibility('packs', pack.id, pack.is_visible)} className={`p-1.5 rounded-lg transition-colors ${pack.is_visible !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid={`pack-visibility-${pack.id}`}>
                      {pack.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setModal({ type: 'pack', data: pack })} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`edit-pack-${pack.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeletePack(pack.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400" data-testid={`delete-pack-${pack.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {packs.length === 0 && <p className="text-center text-white/40 py-8">No packs found</p>}
      </div>
    </div>
  );

  const handleSendDelivery = async (orderId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/orders/${orderId}/deliver`, {}, authHeaders());
      alert(res.data.status === 'sent' ? 'Delivery email sent!' : `Delivery failed: ${res.data.error || 'Unknown error'}`);
      loadData();
    } catch (err) { alert('Failed to send delivery: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleResendDelivery = async (deliveryId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/deliveries/${deliveryId}/resend`, {}, authHeaders());
      alert(res.data.status === 'sent' ? 'Delivery resent!' : `Resend failed: ${res.data.error || 'Unknown error'}`);
      loadData();
    } catch (err) { alert('Failed to resend: ' + (err.response?.data?.detail || err.message)); }
  };

  const renderOrders = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Orders ({orders.length})</h2>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Order ID</th><th className="p-4">Item</th><th className="p-4">Amount</th><th className="p-4">Method</th><th className="p-4">Status</th><th className="p-4">Delivery</th><th className="p-4">Customer</th><th className="p-4">Date</th><th className="p-4">Actions</th>
            </tr></thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openOrderDetail(order.order_id)} data-testid={`order-row-${order.id}`}>
                  <td className="p-4 font-mono text-xs text-white/50">{(order.order_id || '').slice(0, 12)}...</td>
                  <td className="p-4 font-medium">{order.item_name}</td>
                  <td className="p-4 gold-text font-medium">${order.amount}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${order.payment_method==='crypto'?'bg-[#E8720C]/20 text-[#E8720C]':'bg-[#0070BA]/20 text-[#0070BA]'}`}>{(order.payment_method || 'paypal').toUpperCase()}</span></td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${order.payment_status==='paid'?'bg-green-500/20 text-green-400':'bg-yellow-500/20 text-yellow-400'}`}>{order.payment_status}</span></td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.delivery_status === 'sent' ? 'bg-green-500/20 text-green-400' :
                      order.delivery_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-white/10 text-white/40'
                    }`} data-testid={`delivery-status-${order.id}`}>
                      {order.delivery_status === 'sent' ? 'Delivered' : order.delivery_status === 'failed' ? 'Failed' : 'Not sent'}
                    </span>
                  </td>
                  <td className="p-4 text-white/60">{order.user_email || 'Guest'}</td>
                  <td className="p-4 text-white/40">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</td>
                  <td className="p-4">
                    {order.payment_status === 'paid' && (
                      <button onClick={() => handleSendDelivery(order.order_id)}
                        className="px-3 py-1.5 bg-[#E8720C]/10 hover:bg-[#E8720C]/20 text-[#E8720C] rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                        data-testid={`send-delivery-${order.id}`}>
                        <Mail className="w-3 h-3" />{order.delivery_status === 'sent' ? 'Resend' : 'Send'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && <p className="text-center text-white/40 py-8">No orders yet</p>}
      </div>
    </div>
  );

  const renderDeliveries = () => (
    <div>
      <h2 className="text-2xl font-bold mb-2">Delivery Log</h2>
      <p className="text-white/50 text-sm mb-6">Track all automatic email deliveries sent to customers.</p>
      {!emailSettings.configured && (
        <div className="glass-card p-4 mb-6 border border-[#E8720C]/30 bg-[#E8720C]/5">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#E8720C]" />
            <div>
              <p className="text-sm font-medium text-[#E8720C]">Email not configured</p>
              <p className="text-xs text-white/50">Go to Settings and enter your Resend API key to enable automatic delivery emails.</p>
            </div>
          </div>
        </div>
      )}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Order ID</th><th className="p-4">Product</th><th className="p-4">Customer</th><th className="p-4">Status</th><th className="p-4">Sent At</th><th className="p-4">Attempts</th><th className="p-4">Actions</th>
            </tr></thead>
            <tbody>
              {deliveries.map(d => (
                <tr key={d.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 font-mono text-xs text-white/50">{(d.order_id || '').slice(0, 14)}...</td>
                  <td className="p-4">
                    <p className="font-medium text-sm">{d.item_name}</p>
                    <p className="text-[10px] text-white/40">{(d.license_type || 'basic').toUpperCase()} License</p>
                  </td>
                  <td className="p-4">
                    <p className="text-sm">{d.buyer_email}</p>
                    {d.buyer_name && <p className="text-[10px] text-white/40">{d.buyer_name}</p>}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      d.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                      d.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`} data-testid={`delivery-log-status-${d.id}`}>
                      {d.status === 'sent' ? 'Delivered' : d.status === 'failed' ? 'Failed' : 'Pending'}
                    </span>
                    {d.error && <p className="text-[10px] text-red-400 mt-1 max-w-[200px] truncate" title={d.error}>{d.error}</p>}
                  </td>
                  <td className="p-4 text-white/40 text-xs">{d.sent_at ? new Date(d.sent_at).toLocaleString() : '-'}</td>
                  <td className="p-4 text-white/50 text-xs">{d.attempts}</td>
                  <td className="p-4">
                    <button onClick={() => handleResendDelivery(d.id)}
                      className="px-3 py-1.5 bg-[#E8720C]/10 hover:bg-[#E8720C]/20 text-[#E8720C] rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                      data-testid={`resend-delivery-${d.id}`}>
                      <RotateCcw className="w-3 h-3" />Resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {deliveries.length === 0 && <p className="text-center text-white/40 py-8">No deliveries yet. Deliveries are sent automatically after successful payments.</p>}
      </div>
      {deliveries.length > 0 && (
        <div className="mt-4 flex items-center gap-4 text-xs text-white/40">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Delivered: {deliveries.filter(d => d.status === 'sent').length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Failed: {deliveries.filter(d => d.status === 'failed').length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Pending: {deliveries.filter(d => d.status === 'pending').length}</span>
        </div>
      )}
    </div>
  );

  const renderWallets = () => (
    <div>
      <h2 className="text-2xl font-bold mb-2">Wallet Configuration</h2>
      <p className="text-white/50 text-sm mb-8">Manage your cryptocurrency wallet addresses for receiving payments.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {['usdt', 'btc'].map(currency => {
          const wallet = adminWallets.find(w => w.currency === currency);
          return (
            <div key={currency} className="glass-card p-6" data-testid={`wallet-card-${currency}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#E8720C]/20 flex items-center justify-center"><Wallet className="w-5 h-5 text-[#E8720C]" /></div>
                <div><p className="font-bold text-lg">{currency.toUpperCase()}</p><p className="text-xs text-white/40">{currency==='usdt'?'Tether':'Bitcoin'}</p></div>
              </div>
              {wallet ? (<><code className="block bg-white/5 rounded-lg p-3 text-xs font-mono break-all mb-2">{wallet.address}</code>{wallet.network && <p className="text-xs text-white/40">Network: {wallet.network}</p>}</>) : <p className="text-sm text-red-400">Not configured</p>}
            </div>
          );
        })}
      </div>
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Add / Update Wallet</h3>
        <form onSubmit={handleSaveWallet} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-xs text-white/50 mb-1">Currency</label>
              <select className="w-full input-glass" value={walletForm.currency} onChange={e => setWalletForm({...walletForm, currency: e.target.value})} data-testid="wallet-currency-select">
                <option value="usdt" className="bg-[#0a0a0c]">USDT</option><option value="btc" className="bg-[#0a0a0c]">BTC</option>
              </select></div>
            <div><label className="block text-xs text-white/50 mb-1">Network (optional)</label>
              <input className="w-full input-glass" placeholder="TRC20, ERC20, BTC" value={walletForm.network} onChange={e => setWalletForm({...walletForm, network: e.target.value})} data-testid="wallet-network-input" /></div>
            <div className="flex items-end"><button type="submit" className="btn-primary py-3 px-6 text-sm w-full" data-testid="wallet-save-btn"><Save className="w-4 h-4 inline mr-2" />Save Wallet</button></div>
          </div>
          <div><label className="block text-xs text-white/50 mb-1">Wallet Address</label>
            <input className="w-full input-glass font-mono text-sm" placeholder="Enter wallet address..." value={walletForm.address} onChange={e => setWalletForm({...walletForm, address: e.target.value})} required data-testid="wallet-address-input" /></div>
        </form>
      </div>
    </div>
  );

  const renderCrypto = () => {
    const pending = cryptoOrders.filter(o => o.status === 'pending_verification');
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">Crypto Payments ({cryptoOrders.length})</h2>
        {pending.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-[#E8720C]" />Pending Verification ({pending.length})</h3>
            <div className="space-y-4">
              {pending.map(order => (
                <div key={order.order_id} className="glass-card p-6 border-[#E8720C]/20" data-testid={`crypto-order-${order.order_id}`}>
                  {/* CRYPTO VERIFICATION ALERT */}
                  <div className="mb-4 p-4 bg-red-500/10 border-2 border-red-500/40 rounded-xl" data-testid="crypto-verify-alert">
                    <p className="text-red-400 font-bold text-base text-center uppercase tracking-wide">
                      VERIFICA TU BILLETERA PRIMERO ANTES DE APROBAR ESTA SOLICITUD
                    </p>
                  </div>
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm bg-white/10 px-2 py-1 rounded">{order.order_id}</span>
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">pending</span>
                      </div>
                      <p className="font-medium">{order.item_name}</p>
                      <p className="text-sm text-white/50">{order.user_email||'Unknown'} &bull; {order.crypto_method.toUpperCase()} &bull; ${order.amount}</p>
                      {order.proof_value && <div className="mt-2 p-3 bg-white/5 rounded-lg"><p className="text-xs text-white/40 mb-1">Proof (TXID):</p><code className="text-xs font-mono break-all">{order.proof_value}</code></div>}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => handleVerifyCrypto(order.order_id, 'approved')} className="btn-primary py-2 px-4 text-sm flex items-center gap-1" data-testid={`approve-crypto-${order.order_id}`}><CheckCircle className="w-4 h-4" />Approve</button>
                      <button onClick={() => handleVerifyCrypto(order.order_id, 'rejected')} className="btn-secondary py-2 px-4 text-sm flex items-center gap-1 border-red-500/30 text-red-400" data-testid={`reject-crypto-${order.order_id}`}><XCircle className="w-4 h-4" />Reject</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/40 border-b border-white/10">
                <th className="p-4">Order ID</th><th className="p-4">Item</th><th className="p-4">Amount</th><th className="p-4">Crypto</th><th className="p-4">Status</th><th className="p-4">Customer</th><th className="p-4">Date</th>
              </tr></thead>
              <tbody>
                {cryptoOrders.map(order => (
                  <tr key={order.order_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 font-mono text-xs">{order.order_id}</td><td className="p-4">{order.item_name}</td>
                    <td className="p-4 gold-text">${order.amount}</td>
                    <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-white/10">{order.crypto_method.toUpperCase()}</span></td>
                    <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${order.status==='approved'?'bg-green-500/20 text-green-400':order.status==='rejected'?'bg-red-500/20 text-red-400':order.status==='pending_verification'?'bg-yellow-500/20 text-yellow-400':'bg-white/10'}`}>{order.status.replace(/_/g,' ')}</span></td>
                    <td className="p-4 text-white/60">{order.user_email||'-'}</td><td className="p-4 text-white/40">{new Date(order.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cryptoOrders.length === 0 && <p className="text-center text-white/40 py-8">No crypto orders yet</p>}
        </div>
      </div>
    );
  };

  const renderUsers = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Users ({users.length})</h2>
        {selectedUser && <button onClick={() => setSelectedUser(null)} className="text-sm text-white/50 hover:text-white flex items-center gap-1" data-testid="user-back-btn"><X className="w-4 h-4" /> Back to list</button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Users} label="Total Users" value={analytics?.users.total||0} color="blue" />
        <StatCard icon={Activity} label="Online Now" value={analytics?.users.online_now||0} color="green" />
        <StatCard icon={TrendingUp} label="New This Week" value={analytics?.users.new_this_week||0} color="orange" />
      </div>

      {selectedUser ? (
        <div className="space-y-6">
          {userDetailLoading ? <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-white/40" /></div> : (<>
            {/* User Info Card */}
            <div className="glass-card p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Edit3 className="w-5 h-5 text-[#E8720C]" /> Edit User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Name</label>
                  <input value={userEditForm.name} onChange={e => setUserEditForm(f => ({...f, name: e.target.value}))}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50"
                    data-testid="user-edit-name" />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Email</label>
                  <input value={userEditForm.email} onChange={e => setUserEditForm(f => ({...f, email: e.target.value}))}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50"
                    data-testid="user-edit-email" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={handleUpdateUser} className="px-5 py-2 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white" data-testid="user-save-btn">
                  <Save className="w-4 h-4 inline mr-1" /> Save Changes
                </button>
                <span className="text-xs text-white/30">Role: {selectedUser.role} | Joined: {new Date(selectedUser.created_at).toLocaleDateString()} | Total Spent: ${selectedUser.total_spent?.toFixed(2)}</span>
              </div>
            </div>

            {/* Reset Password */}
            <div className="glass-card p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-[#E8720C]" /> Reset Password</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-white/50 mb-1">New Password (min 8 characters)</label>
                  <input type="password" value={userResetPw} onChange={e => setUserResetPw(e.target.value)} placeholder="Enter new password"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                    data-testid="user-reset-pw-input" />
                </div>
                <button onClick={handleResetUserPassword} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-xl text-sm font-semibold text-white" data-testid="user-reset-pw-btn">Reset Password</button>
              </div>
            </div>

            {/* Purchase History */}
            <div className="glass-card p-6">
              <h3 className="font-semibold text-lg mb-4">Purchase History ({selectedUser.purchases?.length || 0})</h3>
              {(!selectedUser.purchases || selectedUser.purchases.length === 0) ? <p className="text-white/40 text-sm">No purchases yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-white/40 border-b border-white/10">
                      <th className="p-3">Product</th><th className="p-3">Type</th><th className="p-3">Amount</th><th className="p-3">Payment</th><th className="p-3">Status</th><th className="p-3">Date</th>
                    </tr></thead>
                    <tbody>{selectedUser.purchases.map((p, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 font-medium">{p.item_name}</td>
                        <td className="p-3"><span className="px-2 py-0.5 bg-white/10 rounded text-xs">{p.item_type}</span></td>
                        <td className="p-3 text-[#E8720C] font-semibold">${p.amount?.toFixed(2)}</td>
                        <td className="p-3 text-white/60">{p.payment_method}{p.payment_last4 ? ` (...${p.payment_last4})` : ''}</td>
                        <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.status === 'paid' || p.status === 'approved' ? 'bg-green-500/15 text-green-400' : p.status === 'pending' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-white/10 text-white/50'}`}>{(p.status || '-').toUpperCase()}</span></td>
                        <td className="p-3 text-white/40">{p.date ? new Date(p.date).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Billing & Account Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-card p-6">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#E8720C]" /> Payment Methods</h3>
                {(!selectedUser.billing_methods || selectedUser.billing_methods.length === 0) ? <p className="text-white/40 text-xs">No methods saved</p> : (
                  <div className="space-y-2">
                    {selectedUser.billing_methods.map((bm, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-white/[0.03] rounded-lg text-xs">
                        <div>
                          <span className="font-medium">{bm.type === 'card' ? 'Card' : bm.type === 'paypal' ? 'PayPal' : 'Crypto'}</span>
                          <span className="text-white/40 ml-2">{bm.detail}</span>
                        </div>
                        {bm.is_default && <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 text-[9px] rounded font-bold">DEFAULT</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-white/40">Auto-Pay:</span>
                  <span className={selectedUser.auto_pay ? 'text-green-400' : 'text-white/30'}>{selectedUser.auto_pay ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="glass-card p-6">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-[#E8720C]" /> Account Summary</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-white/40">Total Spent:</span><span className="text-[#E8720C] font-bold">${selectedUser.total_spent?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Total Orders:</span><span>{selectedUser.total_orders || 0}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Membership:</span><span className={selectedUser.membership?.status === 'active' ? 'text-green-400' : 'text-white/30'}>{selectedUser.membership?.plan || 'None'} ({selectedUser.membership?.status || 'inactive'})</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Country:</span><span>{selectedUser.country || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Joined:</span><span>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '-'}</span></div>
                </div>
              </div>
            </div>
          </>)}
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/40 border-b border-white/10">
                <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Purchases</th><th className="p-4">Joined</th><th className="p-4">Actions</th>
              </tr></thead>
              <tbody>{users.map(u => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 font-medium">{u.name}</td>
                  <td className="p-4 text-white/60">{u.email}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${u.role==='admin'?'bg-[#E8720C]/20 text-[#E8720C]':'bg-white/10'}`}>{u.role}</span></td>
                  <td className="p-4">{u.purchases_count}</td>
                  <td className="p-4 text-white/40">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-4">
                    <button onClick={() => loadUserDetail(u.id)} className="px-3 py-1.5 bg-white/10 hover:bg-[#E8720C]/20 rounded-lg text-xs text-white/70 hover:text-[#E8720C] transition-colors" data-testid={`user-view-${u.id}`}>
                      <Eye className="w-3 h-3 inline mr-1" /> View
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderLeads = () => {
    const allLeads = leads.leads || [];
    const stats = leads.stats || {};
    const filtered = allLeads.filter(l => {
      if (leadFilter === 'verified') return l.verified && !l.converted;
      if (leadFilter === 'unverified') return !l.verified;
      if (leadFilter === 'converted') return l.converted;
      return true;
    });

    return (
      <div data-testid="admin-leads-section">
        <h2 className="text-2xl font-bold mb-6">Leads & Prospects</h2>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-card p-4" data-testid="leads-stat-total">
            <p className="text-2xl font-bold">{stats.total || 0}</p>
            <p className="text-xs text-white/50">Total Leads</p>
          </div>
          <div className="glass-card p-4" data-testid="leads-stat-verified">
            <p className="text-2xl font-bold text-green-400">{stats.verified || 0}</p>
            <p className="text-xs text-white/50">Verified</p>
          </div>
          <div className="glass-card p-4" data-testid="leads-stat-unverified">
            <p className="text-2xl font-bold text-yellow-400">{stats.unverified || 0}</p>
            <p className="text-xs text-white/50">Unverified</p>
          </div>
          <div className="glass-card p-4" data-testid="leads-stat-converted">
            <p className="text-2xl font-bold text-[#E8720C]">{stats.converted || 0}</p>
            <p className="text-xs text-white/50">Converted ({stats.conversion_rate || 0}%)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {['all', 'verified', 'unverified', 'converted'].map(f => (
            <button
              key={f}
              onClick={() => setLeadFilter(f)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${leadFilter === f ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
              data-testid={`leads-filter-${f}`}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? allLeads.length : filtered.length})
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-white/10 text-white/40 text-xs">
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Country</th>
                  <th className="text-center p-4">Verified</th>
                  <th className="text-center p-4">Converted</th>
                  <th className="text-left p-4">Created</th>
                  <th className="text-left p-4">Last Email Sent</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-white/30">No leads found</td></tr>
                ) : filtered.map((l, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`lead-row-${i}`}>
                    <td className="p-4 font-medium">{l.name || 'N/A'}</td>
                    <td className="p-4 text-white/60 font-mono text-xs">{l.email}</td>
                    <td className="p-4 text-white/60">{l.country || 'Unknown'}</td>
                    <td className="p-4 text-center">
                      {l.verified ? (
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-green-500/15 text-green-400 border border-green-500/20">Yes</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">No</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {l.converted ? (
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-[#E8720C]/15 text-[#E8720C] border border-[#E8720C]/20">User</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-white/5 text-white/30 border border-white/10">Lead</span>
                      )}
                    </td>
                    <td className="p-4 text-white/40 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : 'N/A'}</td>
                    <td className="p-4 text-white/40 text-xs">{l.last_verification_sent ? new Date(l.last_verification_sent).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderGeo = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Geographic Analytics</h2>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Globe} label="Visitor Countries" value={geo?.total_countries || 0} color="blue" />
        <StatCard icon={Users} label="Buyer Countries" value={geo?.total_buyer_countries || 0} color="green" />
        <StatCard icon={DollarSign} label="Total Revenue" value={`$${(geo?.total_revenue || 0).toFixed(2)}`} color="orange" />
        <StatCard icon={ShoppingBag} label="Total Purchases" value={geo?.total_purchases || 0} color="purple" />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'visitors', label: 'Visitors', icon: Eye },
          { id: 'buyers', label: 'Buyers', icon: Users },
          { id: 'revenue', label: 'Revenue', icon: DollarSign },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setGeoTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
              geoTab === tab.id
                ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30'
                : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
            }`}
            data-testid={`geo-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Visitors Tab */}
      {geoTab === 'visitors' && (
        <div className="glass-card p-6" data-testid="geo-visitors-panel">
          <h3 className="text-lg font-semibold mb-4">Visitors by Country</h3>
          {(!geo || !geo.visitors_by_country?.length) ? (
            <p className="text-white/40 text-sm">No visitor geographic data yet.</p>
          ) : (
            <div className="space-y-3">
              {geo.visitors_by_country.map((item, i) => {
                const max = geo.visitors_by_country[0]?.visits || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-visitor-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-blue-500/60 to-blue-400" style={{ width: `${(item.visits / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-white/60 w-20 text-right">{item.visits} visits</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.unique_visitors || 0} unique</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Buyers Tab */}
      {geoTab === 'buyers' && (
        <div className="glass-card p-6" data-testid="geo-buyers-panel">
          <h3 className="text-lg font-semibold mb-4">Buyers by Country</h3>
          {(!geo || !geo.buyers_by_country?.length) ? (
            <p className="text-white/40 text-sm">No buyer geographic data yet. Purchases will appear here.</p>
          ) : (
            <div className="space-y-3">
              {geo.buyers_by_country.map((item, i) => {
                const max = geo.buyers_by_country[0]?.purchases || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-buyer-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-green-500/60 to-green-400" style={{ width: `${(item.purchases / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-white/60 w-20 text-right">{item.purchases} orders</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.unique_buyers || 0} buyers</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Revenue Tab */}
      {geoTab === 'revenue' && (
        <div className="glass-card p-6" data-testid="geo-revenue-panel">
          <h3 className="text-lg font-semibold mb-4">Revenue by Country</h3>
          {(!geo || !geo.buyers_by_country?.length) ? (
            <p className="text-white/40 text-sm">No revenue geographic data yet.</p>
          ) : (
            <div className="space-y-3">
              {[...geo.buyers_by_country].sort((a, b) => b.revenue - a.revenue).map((item, i) => {
                const max = [...geo.buyers_by_country].sort((a, b) => b.revenue - a.revenue)[0]?.revenue || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-revenue-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#E8720C]/60 to-[#E8720C]" style={{ width: `${(item.revenue / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-[#E8720C] font-semibold w-24 text-right">${item.revenue.toFixed(2)}</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.purchases} orders</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderMemberships = () => (
    <div>
      <h2 className="text-2xl font-bold mb-2">Membership Plans</h2>
      <p className="text-white/50 text-sm mb-8">Edit pricing, names, download limits, and features for each membership tier.</p>
      <div className="space-y-6">
        {memberships.map(plan => (
          <MembershipEditor key={plan.plan_id} plan={plan} onSave={handleSaveMembership} />
        ))}
      </div>
    </div>
  );

  // ─── Artist Management ─────────────
  const handleSaveArtist = async (data) => {
    try {
      if (data.id) {
        await axios.put(`${API_URL}/api/admin/artists/${data.id}`, data, authHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/artists`, data, authHeaders());
      }
      setArtistModal(null);
      loadData();
    } catch (err) { alert('Error saving artist: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDeleteArtist = async (id) => {
    if (!window.confirm('Delete this artist?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/artists/${id}`, authHeaders());
      loadData();
    } catch (err) { alert('Error deleting artist'); }
  };

  const refreshAllSpotifyImages = async () => {
    if (!window.confirm('Traer fotos oficiales de Spotify para todos los artistas con perfil vinculado?')) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/artists/refresh-spotify-images-all`, {}, authHeaders());
      alert(r.data.message || 'Listo');
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const refreshArtistSpotifyImage = async (artistId) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/artists/${artistId}/refresh-spotify-image`, {}, authHeaders());
      if (r.data?.image) loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Error obteniendo imagen'); }
  };

  const renderArtists = () => {
    const featured = adminArtists.filter(a => a.is_featured);
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Artists ({adminArtists.length})</h2>
            <p className="text-white/50 text-sm">{featured.length} featured</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshAllSpotifyImages}
              className="px-4 py-2 bg-[#1DB954]/15 hover:bg-[#1DB954]/25 border border-[#1DB954]/30 text-[#1DB954] rounded-xl text-xs font-bold flex items-center gap-1.5"
              title="Trae la foto oficial de Spotify para todos los artistas"
              data-testid="refresh-all-spotify-imgs"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Traer Fotos de Spotify
            </button>
            <button onClick={() => setArtistModal({ type: 'create' })} className="btn-primary py-2 px-5 text-sm" data-testid="add-artist-btn">
              <Plus className="w-4 h-4 inline mr-1" />Add Artist
            </button>
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/40 border-b border-white/10">
                <th className="p-4 w-12">#</th><th className="p-4">Image</th><th className="p-4">Name</th><th className="p-4">Genre</th><th className="p-4">Tag</th><th className="p-4">Featured</th><th className="p-4">Links</th><th className="p-4">Actions</th>
              </tr></thead>
              <tbody>
                {adminArtists.map((artist, idx) => (
                  <tr key={artist.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 text-white/30">{idx + 1}</td>
                    <td className="p-4">
                      {artist.image ? (
                        <img src={artist.image} alt={artist.name} className="w-10 h-10 rounded-lg object-cover border border-white/10" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#E8720C]/10 flex items-center justify-center">
                          <span className="text-sm font-bold text-[#E8720C]">{artist.name?.charAt(0)}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4 font-medium">{artist.name}</td>
                    <td className="p-4 text-white/50">{artist.genre}</td>
                    <td className="p-4 text-white/40 text-xs">{artist.tag}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${artist.is_featured ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-white/5 text-white/30'}`} data-testid={`artist-featured-${artist.id}`}>
                        {artist.is_featured ? 'Featured' : 'Hidden'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {artist.spotify_url && <span className="w-2 h-2 rounded-full bg-[#1DB954]" title="Spotify"></span>}
                        {artist.youtube_url && <span className="w-2 h-2 rounded-full bg-[#FF0000]" title="YouTube"></span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {artist.spotify_url && (
                          <button
                            onClick={() => refreshArtistSpotifyImage(artist.id)}
                            className="p-1.5 rounded-lg bg-[#1DB954]/10 hover:bg-[#1DB954]/25 text-[#1DB954]"
                            title="Traer foto oficial de Spotify"
                            data-testid={`refresh-spotify-img-${artist.id}`}
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => setArtistModal({ type: 'edit', data: artist })} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white" data-testid={`edit-artist-${artist.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeleteArtist(artist.id)} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400" data-testid={`delete-artist-${artist.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {adminArtists.length === 0 && <p className="text-center text-white/40 py-8">No artists yet. Add your first artist above.</p>}
        </div>

        {artistModal && (
          <ArtistModal
            artist={artistModal.type === 'edit' ? artistModal.data : null}
            onClose={() => setArtistModal(null)}
            onSave={handleSaveArtist}
            onUpload={handleUploadFile}
          />
        )}
      </div>
    );
  };

  const renderContent = () => (
    <div>
      <h2 className="text-2xl font-bold mb-2">Content Control</h2>
      <p className="text-white/50 text-sm mb-8">Edit homepage sections and manage what appears on the website.</p>
      <ContentEditor section="hero" label="Hero Section" current={siteContent.hero?.content || {}} onSave={handleSaveContent}
        fields={[{key:'title',label:'Title',type:'text'},{key:'subtitle',label:'Subtitle',type:'text'},{key:'cta_text',label:'Button Text',type:'text'}]} />
      <ContentEditor section="about" label="About Page Content" current={siteContent.about?.content || {}} onSave={handleSaveContent} onUpload={handleUploadFile}
        fields={[
          {key:'intro',label:'About Intro Text',type:'textarea'},
          {key:'vision',label:'Vision Statement',type:'textarea'},
          {key:'team_description',label:'Team Description',type:'textarea'},
          {key:'team_image1',label:'Team Image 1 (Production)',type:'image'},
          {key:'team_image2',label:'Team Image 2 (Studio)',type:'image'},
          {key:'team_image3',label:'Team Image 3 (Sessions)',type:'image'},
        ]} />
      <ContentEditor section="promotion" label="Promotion Banner" current={siteContent.promotion?.content || {}} onSave={handleSaveContent}
        fields={[{key:'title',label:'Title',type:'text'},{key:'subtitle',label:'Subtitle',type:'text'},{key:'enabled',label:'Show on Homepage',type:'toggle'}]} />
      <ContentEditor section="announcement" label="Announcement Bar" current={siteContent.announcement?.content || {}} onSave={handleSaveContent}
        fields={[{key:'text',label:'Announcement Text',type:'text'},{key:'enabled',label:'Show Announcement',type:'toggle'}]} />
    </div>
  );


  // ─── Spotify Playlists Management ─────────────

  const loadSpotifyPlaylists = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/spotify-playlists`, authHeaders());
      setSpotifyPlaylists(r.data || []);
    } catch {}
  };

  const handleSaveSpotifyPlaylist = async (data) => {
    try {
      if (data.id) {
        await axios.put(`${API_URL}/api/admin/spotify-playlists/${data.id}`, data, authHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/spotify-playlists`, data, authHeaders());
      }
      setSpModal(null);
      loadSpotifyPlaylists();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleDeleteSpotifyPlaylist = async (id) => {
    if (!window.confirm('Eliminar esta playlist?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/spotify-playlists/${id}`, authHeaders());
      loadSpotifyPlaylists();
    } catch { alert('Error eliminando'); }
  };

  const renderSpotifyPlaylists = () => (
    <div data-testid="spotify-playlists-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Spotify Playlists</h2>
          <p className="text-white/50 text-sm">Playlists que se muestran en el reproductor de la pagina</p>
        </div>
        <button onClick={() => setSpModal({ name: '', spotify_url: '', description: '', order: spotifyPlaylists.length })}
          className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-spotify-playlist-btn">
          <Plus className="w-4 h-4" /> Agregar Playlist
        </button>
      </div>

      {spotifyPlaylists.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <ListMusic className="w-10 h-10 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No hay playlists de Spotify. Agrega una para que aparezca en el reproductor.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {spotifyPlaylists.map(p => (
            <div key={p.id} className="glass-card p-4 flex items-center gap-4" data-testid={`spotify-playlist-${p.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#1DB954]/15 flex items-center justify-center shrink-0">
                <ListMusic className="w-5 h-5 text-[#1DB954]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-white/40 truncate">{p.spotify_url}</div>
                {p.description && <div className="text-xs text-white/30 mt-0.5">{p.description}</div>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setSpModal({ ...p })} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/40" data-testid={`edit-sp-${p.id}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDeleteSpotifyPlaylist(p.id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400" data-testid={`delete-sp-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {spModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSpModal(null)}>
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="spotify-playlist-modal">
            <h3 className="text-lg font-bold mb-4">{spModal.id ? 'Editar' : 'Agregar'} Playlist</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Nombre</label>
                <input className="w-full input-glass" value={spModal.name} onChange={e => setSpModal({ ...spModal, name: e.target.value })}
                  placeholder="Reggaeton Hits OG" data-testid="sp-name-input" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">URL de Spotify</label>
                <input className="w-full input-glass" value={spModal.spotify_url} onChange={e => setSpModal({ ...spModal, spotify_url: e.target.value })}
                  placeholder="https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ" data-testid="sp-url-input" />
                <p className="text-[10px] text-white/30 mt-1">Abre la playlist en Spotify → Click derecho → Compartir → Copiar enlace. Pega el link completo aqui. Ejemplo: https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ</p>
                {spModal.spotify_url && !spModal.spotify_url.includes('open.spotify.com/playlist/') && (
                  <p className="text-[10px] text-red-400 mt-1">El link debe ser de una playlist de Spotify (open.spotify.com/playlist/...)</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Descripcion (opcional)</label>
                <input className="w-full input-glass" value={spModal.description || ''} onChange={e => setSpModal({ ...spModal, description: e.target.value })}
                  placeholder="Las mejores del genero..." />
              </div>
              <div className="flex gap-3">
                <button onClick={() => handleSaveSpotifyPlaylist(spModal)} className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2 text-sm" data-testid="sp-save-btn">
                  <Save className="w-4 h-4" />Guardar
                </button>
                <button onClick={() => setSpModal(null)} className="px-5 py-2.5 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Support unread count (loaded from initial fetch) ─────────────

  // ─── Settings (WhatsApp, PayPal, Email) ─────────────
  const saveWhatsApp = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/whatsapp-config`, { number: settingsForm.whatsappNumber }, authHeaders());
      alert('WhatsApp number saved!');
      loadData();
    } catch { alert('Failed to save'); }
  };
  const savePayPal = async () => {
    if (!settingsForm.paypalClientId || !settingsForm.paypalSecret) { alert('Both PayPal fields are required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/paypal-config`, { client_id: settingsForm.paypalClientId, secret: settingsForm.paypalSecret }, authHeaders());
      alert('PayPal configuration saved!');
      setSettingsForm(f => ({ ...f, paypalSecret: '' }));
      loadData();
    } catch { alert('Failed to save'); }
  };
  const saveEmailSettings = async () => {
    if (!emailForm.api_key) { alert('Resend API key is required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/email-settings`, { api_key: emailForm.api_key, sender_email: emailForm.sender_email }, authHeaders());
      alert('Email settings saved!');
      setEmailForm(f => ({ ...f, api_key: '' }));
      loadData();
    } catch { alert('Failed to save email settings'); }
  };

  const saveEmailSenders = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/email-senders`, { senders: emailSenders }, authHeaders());
      alert('Sender configuration saved!');
      loadData();
    } catch { alert('Failed to save sender configuration'); }
  };

  const addEmailSender = () => {
    setEmailSenders(prev => [...prev, { email: '', label: '', types: [], is_default: prev.length === 0 }]);
  };

  const updateEmailSender = (index, field, value) => {
    setEmailSenders(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const removeEmailSender = (index) => {
    setEmailSenders(prev => prev.filter((_, i) => i !== index));
  };

  const toggleSenderType = (index, type) => {
    setEmailSenders(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const types = s.types.includes(type) ? s.types.filter(t => t !== type) : [...s.types, type];
      return { ...s, types };
    }));
  };

  const setDefaultSender = (index) => {
    setEmailSenders(prev => prev.map((s, i) => ({ ...s, is_default: i === index })));
  };

  // ─── Admin Password Change ─────────────
  const handleChangePassword = async () => {
    if (!pwChangeForm.current || !pwChangeForm.newPw) { alert('Both current and new password are required'); return; }
    if (pwChangeForm.newPw.length < 8) { alert('New password must be at least 8 characters'); return; }
    if (pwChangeForm.newPw !== pwChangeForm.confirm) { alert('New passwords do not match'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/change-password`, { current_password: pwChangeForm.current, new_password: pwChangeForm.newPw }, authHeaders());
      alert('Password changed successfully!');
      setPwChangeForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to change password');
    }
  };

  // ─── User Detail View ─────────────
  const loadUserDetail = async (userId) => {
    setUserDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${userId}`, authHeaders());
      setSelectedUser(res.data);
      setUserEditForm({ name: res.data.name, email: res.data.email });
      setUserResetPw('');
    } catch { alert('Failed to load user details'); }
    finally { setUserDetailLoading(false); }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${selectedUser.id}`, userEditForm, authHeaders());
      alert('User updated');
      loadUserDetail(selectedUser.id);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update user'); }
  };

  const handleResetUserPassword = async () => {
    if (!selectedUser) return;
    if (!userResetPw || userResetPw.length < 8) { alert('Password must be at least 8 characters'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/reset-password`, { new_password: userResetPw }, authHeaders());
      alert(`Password reset for ${selectedUser.email}`);
      setUserResetPw('');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reset password'); }
  };

  // ─── Notification Actions ─────────────
  const markNotificationRead = async (id) => {
    await axios.post(`${API_URL}/api/admin/notifications/${id}/read`, {}, authHeaders());
    loadNotifications();
  };

  const markAllRead = async () => {
    await axios.post(`${API_URL}/api/admin/notifications/read-all`, {}, authHeaders());
    loadNotifications();
  };

  // ─── Bulk Track Upload ─────────────
  const handleBulkUpload = async () => {
    if (!bulkFiles.length) return;
    setBulkUploading(true);
    try {
      const mp3s = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.mp3'));
      const wavs = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.wav'));
      const getBase = (n) => n.replace(/\.[^/.]+$/, '').trim().toLowerCase();

      // Upload ONE file at a time for maximum reliability (bypasses ingress body size limits)
      const allUploaded = [];
      const allErrors = [];
      setBulkProgress({ current: 0, total: bulkFiles.length });

      for (let i = 0; i < bulkFiles.length; i++) {
        const file = bulkFiles[i];
        const formData = new FormData();
        formData.append('files', file);
        try {
          // Do NOT set Content-Type manually — axios will set it with the correct boundary for FormData
          const res = await axios.post(`${API_URL}/api/admin/tracks/bulk-upload`, formData, {
            withCredentials: true,
            timeout: 180000,
          });
          if (res.data.uploaded) allUploaded.push(...res.data.uploaded);
          if (res.data.errors) allErrors.push(...res.data.errors);
        } catch (err) {
          console.error('Bulk upload failed for', file.name, err.response?.status, err.response?.data, err.message);
          allErrors.push({ filename: file.name, error: err.response?.data?.detail || err.message || 'Error de conexion' });
        }
        setBulkProgress({ current: i + 1, total: bulkFiles.length });
      }

      const mp3Results = allUploaded.filter(u => (u.format || '').toUpperCase() === 'MP3');
      const wavResults = allUploaded.filter(u => (u.format || '').toUpperCase() === 'WAV');

      const paired = [];
      mp3Results.forEach(mp3 => {
        const base = getBase(mp3.original_filename || mp3.filename || '');
        const wavMatch = wavResults.find(w => getBase(w.original_filename || w.filename || '') === base);
        paired.push({
          ...mp3,
          title: mp3.suggested_title || base,
          artist: 'OG MUSIC',
          price: batchSettings.price || 29.99,
          genre: batchSettings.genre || '',
          bpm: batchSettings.bpm || '',
          includes_playlist: batchSettings.includes_playlist || false,
          license_type: 'basic',
          is_visible: true,
          original_filename: mp3.original_filename || mp3.filename,
          wav_url: wavMatch ? wavMatch.url : '',
        });
      });

      wavResults.forEach(wav => {
        const base = getBase(wav.original_filename || wav.filename || '');
        const hasMp3 = mp3Results.some(m => getBase(m.original_filename || m.filename || '') === base);
        if (!hasMp3) {
          paired.push({
            ...wav,
            title: wav.suggested_title || base,
            artist: 'OG MUSIC',
            price: batchSettings.price || 29.99,
            genre: batchSettings.genre || '',
            bpm: batchSettings.bpm || '',
            includes_playlist: batchSettings.includes_playlist || false,
            license_type: 'basic',
            is_visible: true,
            original_filename: wav.original_filename || wav.filename,
            wav_url: wav.url,
            url: '',
          });
        }
      });

      setBulkUploaded(paired);
      setBulkProgress({ current: 0, total: 0 });
      if (allErrors.length) {
        alert(`${allUploaded.length} subidos. ${allErrors.length} con error:\n${allErrors.slice(0, 5).map(e => `${e.filename}: ${e.error}`).join('\n')}`);
      }
    } catch (err) {
      alert(`Error subiendo: ${err.response?.data?.detail || err.message}`);
    }
    finally { setBulkUploading(false); setBulkFiles([]); }
  };

  const handleBulkCreate = async () => {
    setBulkCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/tracks/bulk-create`, {
        tracks: bulkUploaded.map(t => ({
          title: t.title,
          artist: t.artist,
          price: parseFloat(t.price),
          genre: t.genre,
          bpm: t.bpm ? parseInt(t.bpm) : null,
          includes_playlist: t.includes_playlist || false,
          license_type: t.license_type,
          audio_preview_url: t.url,
          preview_url: t.preview_url || '',
          wav_file_url: t.wav_url || '',
          is_visible: t.is_visible,
          original_filename: t.original_filename || t.filename,
          format: t.format,
        })),
      }, authHeaders());
      if (res.data.errors?.length) {
        alert(`${res.data.total} tracks created, but ${res.data.errors.length} failed:\n${res.data.errors.map(e => `${e.title}: ${e.error}`).join('\n')}`);
      } else {
        alert(`${res.data.total} tracks created successfully!`);
      }
      setBulkUploaded([]);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Failed to create tracks: ${detail}`);
    }
    finally { setBulkCreating(false); }
  };

  const updateBulkTrack = (idx, field, value) => {
    setBulkUploaded(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const applyBatchSettings = () => {
    setBulkUploaded(prev => prev.map(t => ({
      ...t,
      bpm: batchSettings.bpm || t.bpm,
      price: batchSettings.price || t.price,
      genre: batchSettings.genre || t.genre,
      includes_playlist: batchSettings.includes_playlist,
    })));
  };

  const saveSocialSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/social-settings`, socialSettings, authHeaders());
      alert('Social media settings saved!');
      loadData();
    } catch { alert('Failed to save social settings'); }
  };

  // ─── FAQ Management ─────────────
  const handleSaveFAQ = async (item) => {
    try {
      if (item.id) {
        await apiCall('put', `/api/admin/faq/${item.id}`, item);
      } else {
        await apiCall('post', '/api/admin/faq', item);
      }
      setFaqModal(null);
      loadData();
    } catch { alert('Failed to save FAQ item'); }
  };

  const handleDeleteFAQ = async (id) => {
    if (!window.confirm('Delete this FAQ item?')) return;
    try {
      await apiCall('delete', `/api/admin/faq/${id}`);
      loadData();
    } catch { alert('Failed to delete FAQ item'); }
  };

  const handleMoveFAQ = async (index, direction) => {
    const items = [...faqItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setFaqItems(items);
    try {
      await apiCall('post', '/api/admin/faq/reorder', { ids: items.map(i => i.id) });
    } catch { alert('Failed to reorder'); }
  };

  const renderFAQ = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">FAQ Management</h2>
          <p className="text-white/50 text-sm">Manage frequently asked questions displayed on your site.</p>
        </div>
        <button onClick={() => setFaqModal({ question: '', answer: '' })} className="btn-primary py-2 px-4 text-sm flex items-center gap-2" data-testid="admin-add-faq">
          <Plus className="w-4 h-4" />Add Question
        </button>
      </div>

      {faqItems.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <HelpCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/40">No FAQ items yet. Add your first question.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-faq-list">
          {faqItems.map((item, i) => (
            <div key={item.id} className="glass-card p-5 flex items-start gap-4" data-testid={`admin-faq-item-${i}`}>
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => handleMoveFAQ(i, -1)} disabled={i === 0} className="p-1 hover:bg-white/10 rounded disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => handleMoveFAQ(i, 1)} disabled={i === faqItems.length - 1} className="p-1 hover:bg-white/10 rounded disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm mb-1">{item.question}</p>
                <p className="text-xs text-white/50 line-clamp-2">{item.answer}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setFaqModal(item)} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`admin-faq-edit-${i}`}><Edit3 className="w-4 h-4 text-white/60" /></button>
                <button onClick={() => handleDeleteFAQ(item.id)} className="p-2 hover:bg-red-500/10 rounded-lg" data-testid={`admin-faq-delete-${i}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAQ Modal */}
      {faqModal && (
        <FAQModal item={faqModal} onClose={() => setFaqModal(null)} onSave={handleSaveFAQ} />
      )}
    </div>
  );

  // ─── License Template Management ─────────────
  const handleSaveLicenseTemplate = async (data) => {
    try {
      await apiCall('put', '/api/admin/license-template', data);
      loadData();
      alert('License template saved!');
    } catch { alert('Failed to save license template'); }
  };

  const handlePreviewLicense = async (licenseType) => {
    try {
      const resp = await axios.post(`${API_URL}/api/admin/license-template/preview`, { license_type: licenseType }, { ...authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { alert('Failed to generate preview'); }
  };

  const renderLicenses = () => (
    <div>
      <h2 className="text-2xl font-bold mb-1">License Template</h2>
      <p className="text-white/50 text-sm mb-8">Configure the license certificate that is automatically generated and sent to buyers after purchase.</p>
      <LicenseTemplateEditor
        template={licenseTemplate}
        onSave={handleSaveLicenseTemplate}
        onPreview={handlePreviewLicense}
      />
    </div>
  );

  // ─── AI Settings Management ─────────────
  const handleSaveAISettings = async (data) => {
    try {
      await apiCall('put', '/api/admin/ai-settings', data);
      loadData();
      alert('AI settings saved!');
    } catch { alert('Failed to save AI settings'); }
  };

  const renderAI = () => (
    <div>
      <h2 className="text-2xl font-bold mb-1">AI Sales Assistant</h2>
      <p className="text-white/50 text-sm mb-8">Configure the AI assistant that acts as your sales closer on the website and chat.</p>
      <AISettingsEditor settings={aiSettings} onSave={handleSaveAISettings} />
    </div>
  );

  const renderSettings = () => (
    <div>
      <h2 className="text-2xl font-bold mb-2">Settings</h2>
      <p className="text-white/50 text-sm mb-8">Configure payment integrations, email delivery, and account security.</p>

      <div className="space-y-8">
        {/* Distribution Guide Upload (Ultimate Pack exclusive) */}
        <DistributionGuidePanel />

        {/* PayPal Recurring Subscriptions (Mensual OG Mastering) */}
        <PaypalSubscriptionsPanel />
        {/* Track Capacity Control */}
        <div className="glass-card p-6" data-testid="settings-capacity">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
              <Music className="w-5 h-5 text-[#E8720C]" />
            </div>
            <div>
              <h3 className="font-semibold">Capacidad de Tracks</h3>
              <p className="text-xs text-white/40">Controla el maximo de tracks visibles en la pagina</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-white/5 rounded-xl text-center">
              <p className="text-2xl font-bold text-[#E8720C]">{trackCapacity.visible}</p>
              <p className="text-[10px] text-white/40">Visibles</p>
            </div>
            <div className="p-3 bg-white/5 rounded-xl text-center">
              <p className="text-2xl font-bold">{trackCapacity.limit}</p>
              <p className="text-[10px] text-white/40">Limite Actual</p>
            </div>
            <div className="p-3 bg-white/5 rounded-xl text-center">
              <p className="text-2xl font-bold text-green-400">{trackCapacity.remaining}</p>
              <p className="text-[10px] text-white/40">Disponibles</p>
            </div>
          </div>
          <div className="flex gap-3 max-w-md">
            <input type="number" value={newCapacityLimit} onChange={e => setNewCapacityLimit(e.target.value)}
              className="w-32 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50"
              data-testid="capacity-limit-input" />
            <button onClick={async () => {
              try {
                await axios.put(`${API_URL}/api/admin/track-capacity`, { limit: parseInt(newCapacityLimit) }, authHeaders());
                const r = await axios.get(`${API_URL}/api/admin/track-capacity`, authHeaders());
                setTrackCapacity(r.data);
                alert('Limite actualizado');
              } catch (e) { alert(e.response?.data?.detail || 'Error'); }
            }} className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white" data-testid="capacity-save-btn">
              Guardar Limite
            </button>
          </div>
        </div>

        {/* Google Account Link */}
        <div className="glass-card p-6" data-testid="settings-google-link">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold">Vincular con Google</h3>
              <p className="text-xs text-white/40">Conecta tu cuenta de Google para login rapido</p>
            </div>
          </div>
          {user?.google_linked ? (
            <div className="flex items-center gap-3 p-3 bg-green-500/5 border border-green-500/20 rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-sm text-green-400 font-medium">Google Vinculado</p>
                <p className="text-[10px] text-white/30">Puedes iniciar sesion con Google</p>
              </div>
            </div>
          ) : (
            <button onClick={() => linkGoogleAccount()}
              className="px-6 py-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl text-sm font-medium text-white flex items-center gap-3 transition-colors"
              data-testid="link-google-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Vincular Cuenta de Google
            </button>
          )}
        </div>

        {/* Employee Performance Metrics */}
        {employeeMetrics.length > 0 && (
          <div className="glass-card p-6" data-testid="settings-employee-metrics">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold">Desempeno por Empleado</h3>
                <p className="text-xs text-white/40">Metricas de subida por dia, semana, mes y ano</p>
              </div>
            </div>
            <div className="space-y-4">
              {employeeMetrics.map(emp => (
                <div key={emp.id} className="p-4 bg-white/[0.03] rounded-xl border border-white/5" data-testid={`employee-metric-${emp.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-xs text-white/40">{emp.email}</p>
                    </div>
                    <span className="text-xs text-white/30">{emp.packs} packs</span>
                  </div>
                  <div className="grid grid-cols-5 gap-3 mb-3">
                    {[
                      { label: 'Hoy', val: emp.uploads.today, color: 'text-blue-400' },
                      { label: 'Semana', val: emp.uploads.week, color: 'text-purple-400' },
                      { label: 'Mes', val: emp.uploads.month, color: 'text-green-400' },
                      { label: 'Ano', val: emp.uploads.year, color: 'text-yellow-400' },
                      { label: 'Total', val: emp.uploads.total, color: 'text-[#E8720C]' },
                    ].map((m, i) => (
                      <div key={i} className="text-center">
                        <p className={`text-lg font-bold ${m.color}`}>{m.val}</p>
                        <p className="text-[9px] text-white/30">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-green-400">{emp.approvals.approved} aprobados</span>
                    <span className="text-yellow-400">{emp.approvals.pending} pendientes</span>
                    <span className="text-red-400">{emp.approvals.rejected} rechazados</span>
                  </div>

                  {/* Production metrics for this employee */}
                  {prodMetrics.find(m => m.email === emp.email)?.production && (() => {
                    const p = prodMetrics.find(m => m.email === emp.email).production;
                    return (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[9px] text-[#E8720C] font-bold uppercase tracking-wider mb-2">Produccion Musical</p>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { label: 'Solicitudes', val: p.solicitudes, color: 'text-white/70' },
                            { label: 'Descargados', val: p.descargados, color: 'text-purple-400' },
                            { label: 'En Mastering', val: p.en_mastering, color: 'text-red-400' },
                            { label: 'Masterizados', val: p.masterizados, color: 'text-blue-400' },
                            { label: 'Pend. Aprob.', val: p.pendiente_aprobacion, color: 'text-orange-400' },
                            { label: 'Publicados', val: p.publicados, color: 'text-green-400' },
                            { label: 'Empresa', val: p.archivo_empresa, color: 'text-purple-400' },
                          ].map((m, i) => (
                            <div key={i} className="text-center p-1.5 bg-white/[0.02] rounded-lg">
                              <p className={`text-sm font-bold ${m.color}`}>{m.val}</p>
                              <p className="text-[8px] text-white/30">{m.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Production Metrics Summary (even without traditional employee metrics) */}
        {prodMetrics.length > 0 && (
          <div className="glass-card p-6" data-testid="settings-prod-metrics">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
                <Music className="w-5 h-5 text-[#E8720C]" />
              </div>
              <div>
                <h3 className="font-semibold">Metricas de Produccion Musical</h3>
                <p className="text-xs text-white/40">Rendimiento de cada empleado en el pipeline de produccion</p>
              </div>
            </div>
            <div className="space-y-4">
              {prodMetrics.map(m => (
                <div key={m.email} className="p-4 bg-white/[0.03] rounded-xl border border-white/5" data-testid={`prod-metric-${m.email}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">{m.name}</p>
                      <p className="text-xs text-white/40">{m.email}</p>
                    </div>
                    <span className="text-[10px] text-white/30 uppercase">{m.role}</span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                    {[
                      { label: 'Solicitudes', val: m.production.solicitudes, color: 'text-white/70' },
                      { label: 'Descargados', val: m.production.descargados, color: 'text-purple-400' },
                      { label: 'En Mastering', val: m.production.en_mastering, color: 'text-red-400' },
                      { label: 'Masterizados', val: m.production.masterizados, color: 'text-blue-400' },
                      { label: 'Pend. Aprob.', val: m.production.pendiente_aprobacion, color: 'text-orange-400' },
                      { label: 'Publicados', val: m.production.publicados, color: 'text-green-400' },
                      { label: 'Empresa', val: m.production.archivo_empresa, color: 'text-purple-400' },
                    ].map((s, i) => (
                      <div key={i} className="text-center p-2 bg-white/[0.02] rounded-lg">
                        <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                        <p className="text-[8px] text-white/30">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Password Change */}
        <div className="glass-card p-6" data-testid="settings-password">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Settings className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold">Change Admin Password</h3>
              <p className="text-xs text-white/40">Update your admin account password securely</p>
            </div>
          </div>
          <div className="space-y-3 max-w-md">
            <div>
              <label className="block text-xs text-white/50 mb-1">Current Password</label>
              <input type="password" value={pwChangeForm.current} onChange={e => setPwChangeForm(f => ({...f, current: e.target.value}))}
                placeholder="Enter current password"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="settings-current-pw" />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">New Password (min 8 characters)</label>
              <input type="password" value={pwChangeForm.newPw} onChange={e => setPwChangeForm(f => ({...f, newPw: e.target.value}))}
                placeholder="Enter new password"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="settings-new-pw" />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">Confirm New Password</label>
              <input type="password" value={pwChangeForm.confirm} onChange={e => setPwChangeForm(f => ({...f, confirm: e.target.value}))}
                placeholder="Confirm new password"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="settings-confirm-pw" />
            </div>
            <button onClick={handleChangePassword} className="px-6 py-2.5 bg-red-600 hover:bg-red-700 rounded-xl text-sm font-semibold text-white" data-testid="settings-change-pw-btn">Change Password</button>
          </div>
        </div>

        {/* Email / Resend */}
        <div className="glass-card p-6" data-testid="settings-email">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#E8720C]" />
            </div>
            <div>
              <h3 className="font-semibold">Email Delivery (Resend)</h3>
              <p className="text-xs text-white/40">
                {emailSettings.configured
                  ? `Configured (key: ${emailSettings.api_key}) | Sender: ${emailSettings.sender_email}`
                  : 'Not configured - Enter your Resend API key to enable automatic delivery emails'}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-white/50 mb-1">Resend API Key</label>
              <input value={emailForm.api_key} onChange={e => setEmailForm(f => ({ ...f, api_key: e.target.value }))}
                placeholder={emailSettings.configured ? 'Enter new key to update...' : 're_xxxxxxxxxxxx'}
                type="password"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="settings-resend-key" />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">Sender Email</label>
              <input value={emailForm.sender_email} onChange={e => setEmailForm(f => ({ ...f, sender_email: e.target.value }))}
                placeholder="onboarding@resend.dev (default) or your@domain.com"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="settings-sender-email" />
              <p className="text-[10px] text-white/30 mt-1">Free tier: use onboarding@resend.dev. With verified domain: use your branded email.</p>
            </div>
            <button onClick={saveEmailSettings} className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white" data-testid="settings-email-save">Save Email Settings</button>
          </div>
        </div>

        {/* Multi-Sender Configuration */}
        <div className="glass-card p-6" data-testid="settings-senders">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold">Sender Configuration</h3>
                <p className="text-xs text-white/40">Assign different sender emails per email type</p>
              </div>
            </div>
            <button onClick={addEmailSender} className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/70 font-medium" data-testid="add-sender-btn">+ Add Sender</button>
          </div>

          {emailSenders.length === 0 ? (
            <p className="text-white/30 text-sm py-4 text-center">No senders configured. Add one to get started.</p>
          ) : (
            <div className="space-y-4">
              {emailSenders.map((sender, idx) => (
                <div key={idx} className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input value={sender.email} onChange={e => updateEmailSender(idx, 'email', e.target.value)} placeholder="sender@yourdomain.com"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                        data-testid={`sender-email-${idx}`} />
                      <input value={sender.label} onChange={e => updateEmailSender(idx, 'label', e.target.value)} placeholder="Label (e.g. Delivery, Support)"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                        data-testid={`sender-label-${idx}`} />
                    </div>
                    <button onClick={() => removeEmailSender(idx)} className="p-2 hover:bg-red-500/10 rounded-lg text-red-400/60 hover:text-red-400" data-testid={`remove-sender-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-white/40 uppercase tracking-wider mr-1">Types:</span>
                    {(emailSettings.email_types || ['password_reset','order_confirmation','delivery','support','notification']).map(type => (
                      <button key={type} onClick={() => toggleSenderType(idx, type)}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${sender.types?.includes(type) ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'}`}
                        data-testid={`sender-type-${idx}-${type}`}>
                        {type.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setDefaultSender(idx)}
                      className={`px-3 py-1 rounded-md text-[10px] font-semibold ${sender.is_default ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'}`}
                      data-testid={`sender-default-${idx}`}>
                      {sender.is_default ? 'DEFAULT' : 'Set as Default'}
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={saveEmailSenders} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm font-semibold text-white" data-testid="save-senders-btn">Save Sender Config</button>
            </div>
          )}
        </div>

        {/* PayPal */}
        <div className="glass-card p-6" data-testid="settings-paypal">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#0070BA]/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#0070BA]" />
            </div>
            <div>
              <h3 className="font-semibold">PayPal</h3>
              <p className="text-xs text-white/40">Primary payment method {settings.paypal?.secret ? `(configured: ...${settings.paypal.secret})` : '(not configured)'}</p>
            </div>
          </div>
          <div className="space-y-3">
            <input value={settingsForm.paypalClientId} onChange={e => setSettingsForm(f => ({ ...f, paypalClientId: e.target.value }))}
              placeholder="Client ID"
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
              data-testid="settings-paypal-client-id" />
            <input value={settingsForm.paypalSecret} onChange={e => setSettingsForm(f => ({ ...f, paypalSecret: e.target.value }))}
              placeholder="Secret"
              type="password"
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
              data-testid="settings-paypal-secret" />
            <button onClick={savePayPal} className="px-6 py-2.5 bg-[#0070BA] hover:bg-[#005EA6] rounded-xl text-sm font-semibold text-white" data-testid="settings-paypal-save">Save PayPal Keys</button>
          </div>
        </div>

        {/* Social Media */}
        <div className="glass-card p-6" data-testid="settings-social">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#E8720C]" />
            </div>
            <div>
              <h3 className="font-semibold">Social Media</h3>
              <p className="text-xs text-white/40">Manage your social media presence. Links appear in the footer.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Instagram URL</label>
                <input value={socialSettings.instagram_url} onChange={e => setSocialSettings(s => ({...s, instagram_url: e.target.value}))}
                  placeholder="https://instagram.com/OGMUSICGROUP_" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-instagram-url" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Instagram Handle</label>
                <input value={socialSettings.instagram_handle} onChange={e => setSocialSettings(s => ({...s, instagram_handle: e.target.value}))}
                  placeholder="@OGMUSICGROUP_" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-instagram-handle" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">TikTok URL</label>
                <input value={socialSettings.tiktok_url} onChange={e => setSocialSettings(s => ({...s, tiktok_url: e.target.value}))}
                  placeholder="https://tiktok.com/@OG.MUSIC.GROUP" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-tiktok-url" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">TikTok Handle</label>
                <input value={socialSettings.tiktok_handle} onChange={e => setSocialSettings(s => ({...s, tiktok_handle: e.target.value}))}
                  placeholder="OG.MUSIC.GROUP" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-tiktok-handle" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">YouTube URL</label>
                <input value={socialSettings.youtube_url} onChange={e => setSocialSettings(s => ({...s, youtube_url: e.target.value}))}
                  placeholder="https://youtube.com/@OGMUSICGROUP" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-youtube-url" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">YouTube Handle</label>
                <input value={socialSettings.youtube_handle} onChange={e => setSocialSettings(s => ({...s, youtube_handle: e.target.value}))}
                  placeholder="@OGMUSICGROUP" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-youtube-handle" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Spotify URL (optional)</label>
                <input value={socialSettings.spotify_url} onChange={e => setSocialSettings(s => ({...s, spotify_url: e.target.value}))}
                  placeholder="https://open.spotify.com/..." className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-spotify-url" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Spotify Handle (optional)</label>
                <input value={socialSettings.spotify_handle} onChange={e => setSocialSettings(s => ({...s, spotify_handle: e.target.value}))}
                  placeholder="OG MUSIC GROUP" className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="social-input-spotify-handle" />
              </div>
            </div>
            <button onClick={saveSocialSettings} className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white" data-testid="settings-social-save">Save Social Links</button>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Admin Translation ─────────────
  const adminT = {
    en: {
      panel: 'Admin Panel', overview: 'Overview', tracks: 'Tracks', packs: 'Packs',
      orders: 'Orders', plans: 'Plans', wallets: 'Wallets', crypto: 'Crypto',
      users: 'Users', geography: 'Geography', content: 'Content', support: 'Support',
      settings: 'Settings', media: 'Media', banners: 'Banners', refresh: 'Refresh Data',
      upload: 'Upload', save: 'Save', delete: 'Delete', edit: 'Edit', add: 'Add',
      title: 'Title', subtitle: 'Subtitle', active: 'Active', inactive: 'Inactive',
      tag: 'Tag', link: 'Link', image: 'Image', noItems: 'No items yet',
      mediaLibrary: 'Media Library', mediaDesc: 'Upload and manage images for covers, banners, and promotional content.',
      uploadImage: 'Upload Image', dragDrop: 'Click to upload or drag and drop',
      bannerSystem: 'Promotional Banners', bannerDesc: 'Manage homepage banner slider with album artwork.',
      addBanner: 'Add Banner', selectImage: 'Select from Media', preview: 'Preview',
      reorder: 'Drag to reorder', size: 'Size', date: 'Date', tags: 'Tags', actions: 'Actions',
      createBanner: 'Create Banner', editBanner: 'Edit Banner', imageUrl: 'Image URL',
      noBanners: 'No banners yet. Create one to showcase album artwork on the homepage.',
      noMedia: 'No images uploaded yet. Upload album covers and promotional images.',
      confirmDelete: 'Are you sure?', used: 'Used in'
    },
    es: {
      panel: 'Panel Admin', overview: 'Resumen', tracks: 'Tracks', packs: 'Packs',
      orders: 'Pedidos', plans: 'Planes', wallets: 'Billeteras', crypto: 'Crypto',
      users: 'Usuarios', geography: 'Geografía', content: 'Contenido', support: 'Soporte',
      settings: 'Ajustes', media: 'Medios', banners: 'Banners', refresh: 'Actualizar',
      upload: 'Subir', save: 'Guardar', delete: 'Eliminar', edit: 'Editar', add: 'Agregar',
      title: 'Título', subtitle: 'Subtítulo', active: 'Activo', inactive: 'Inactivo',
      tag: 'Etiqueta', link: 'Enlace', image: 'Imagen', noItems: 'Sin elementos',
      mediaLibrary: 'Biblioteca de Medios', mediaDesc: 'Sube y organiza imágenes para portadas, banners y contenido promocional.',
      uploadImage: 'Subir Imagen', dragDrop: 'Haz clic para subir o arrastra y suelta',
      bannerSystem: 'Banners Promocionales', bannerDesc: 'Administra el banner del homepage con portadas de álbumes.',
      addBanner: 'Agregar Banner', selectImage: 'Seleccionar de Medios', preview: 'Vista previa',
      reorder: 'Arrastra para reordenar', size: 'Tamaño', date: 'Fecha', tags: 'Etiquetas', actions: 'Acciones',
      createBanner: 'Crear Banner', editBanner: 'Editar Banner', imageUrl: 'URL de Imagen',
      noBanners: 'No hay banners. Crea uno para mostrar portadas en el homepage.',
      noMedia: 'No hay imágenes. Sube portadas de álbumes e imágenes promocionales.',
      confirmDelete: '¿Estás seguro?', used: 'Usado en'
    }
  };
  const at = (key) => (adminT[adminLang] || adminT.en)[key] || adminT.en[key] || key;

  // ─── Media Library ─────────────
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^.]+$/, ''));
    try {
      await axios.post(`${API_URL}/api/admin/media/upload`, formData, { ...authHeaders(), headers: { 'Content-Type': 'multipart/form-data' } });
      loadData();
    } catch (err) { alert('Upload failed'); }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm(at('confirmDelete'))) return;
    await apiCall('delete', `/api/admin/media/${id}`);
    loadData();
  };

  const renderMedia = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">{at('mediaLibrary')}</h2>
          <p className="text-white/50 text-sm mt-1">{at('mediaDesc')}</p>
        </div>
        <label className="flex items-center gap-2 px-4 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold cursor-pointer transition-colors" data-testid="media-upload-btn">
          <Upload className="w-4 h-4" /> {at('uploadImage')}
          <input type="file" accept="image/*" className="hidden" onChange={handleMediaUpload} />
        </label>
      </div>

      {mediaItems.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Image className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">{at('noMedia')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {mediaItems.map(m => (
            <div key={m.id} className="glass-card overflow-hidden group relative" data-testid={`media-item-${m.id}`}>
              <div className="aspect-square relative overflow-hidden">
                <img src={`${API_URL}${m.url}`} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(m.url); alert('URL copied!'); }}
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-white" title="Copy URL">
                    <FileText className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteMedia(m.id)}
                    className="w-9 h-9 bg-red-500/20 hover:bg-red-500/40 rounded-lg flex items-center justify-center text-red-400" title={at('delete')}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs font-medium truncate">{m.title}</p>
                <p className="text-[10px] text-white/30 mt-1">{(m.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── Banners ─────────────

  const openBannerModal = (banner) => {
    if (banner) {
      setBannerForm({ title: banner.title, subtitle: banner.subtitle, image_url: banner.image_url, link: banner.link, tag: banner.tag, active: banner.active });
      setBannerModal(banner.id);
    } else {
      setBannerForm({ title: '', subtitle: '', image_url: '', link: '', tag: '', active: true });
      setBannerModal('new');
    }
  };

  const saveBanner = async () => {
    if (!bannerForm.title || !bannerForm.image_url) { alert('Title and image are required'); return; }
    try {
      if (bannerModal === 'new') {
        await apiCall('post', '/api/admin/banners', bannerForm);
      } else {
        await apiCall('put', `/api/admin/banners/${bannerModal}`, bannerForm);
      }
      setBannerModal(null);
      loadData();
    } catch { alert('Failed to save'); }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm(at('confirmDelete'))) return;
    await apiCall('delete', `/api/admin/banners/${id}`);
    loadData();
  };

  const toggleBannerActive = async (id, current) => {
    await apiCall('put', `/api/admin/banners/${id}`, { active: !current });
    loadData();
  };

  const renderBanners = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">{at('bannerSystem')}</h2>
          <p className="text-white/50 text-sm mt-1">{at('bannerDesc')}</p>
        </div>
        <button onClick={() => openBannerModal(null)} className="flex items-center gap-2 px-4 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold transition-colors" data-testid="add-banner-btn">
          <Plus className="w-4 h-4" /> {at('addBanner')}
        </button>
      </div>

      {bannerItems.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Layers className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">{at('noBanners')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bannerItems.map((b, idx) => (
            <div key={b.id} className="glass-card p-4 flex items-center gap-4" data-testid={`banner-item-${b.id}`}>
              <div className="text-white/20"><GripVertical className="w-5 h-5" /></div>
              <div className="w-20 h-14 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                {b.image_url && <img src={`${API_URL}${b.image_url}`} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm truncate">{b.title}</h4>
                  {b.tag && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${b.tag === 'NEW' ? 'bg-[#E8720C]/20 text-[#E8720C]' : b.tag === 'HOT' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/60'}`}>{b.tag}</span>}
                </div>
                {b.subtitle && <p className="text-xs text-white/40 truncate">{b.subtitle}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleBannerActive(b.id, b.active)}
                  className={`px-3 py-1 rounded-full text-[10px] font-bold ${b.active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}>
                  {b.active ? at('active') : at('inactive')}
                </button>
                <button onClick={() => openBannerModal(b)} className="w-8 h-8 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteBanner(b.id)} className="w-8 h-8 bg-red-500/10 hover:bg-red-500/20 rounded-lg flex items-center justify-center text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Banner Modal */}
      {bannerModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-lg p-6" data-testid="banner-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">{bannerModal === 'new' ? at('createBanner') : at('editBanner')}</h3>
              <button onClick={() => setBannerModal(null)} className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-white/50 mb-1 block">{at('title')} *</label>
                <input value={bannerForm.title} onChange={e => setBannerForm(f => ({...f, title: e.target.value}))}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50"
                  data-testid="banner-title-input" />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1 block">{at('subtitle')}</label>
                <input value={bannerForm.subtitle} onChange={e => setBannerForm(f => ({...f, subtitle: e.target.value}))}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50" />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1 block">{at('image')} *</label>
                <div className="flex gap-2">
                  <input value={bannerForm.image_url} onChange={e => setBannerForm(f => ({...f, image_url: e.target.value}))}
                    placeholder="/api/uploads/images/..."
                    className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50"
                    data-testid="banner-image-input" />
                  <button onClick={() => setShowMediaPicker(!showMediaPicker)} className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs hover:bg-white/10">
                    <Image className="w-4 h-4" />
                  </button>
                </div>
                {showMediaPicker && mediaItems.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 bg-white/5 rounded-xl">
                    {mediaItems.map(m => (
                      <button key={m.id} onClick={() => { setBannerForm(f => ({...f, image_url: m.url})); setShowMediaPicker(false); }}
                        className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#E8720C] transition-colors">
                        <img src={`${API_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                {bannerForm.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden h-32">
                    <img src={`${API_URL}${bannerForm.image_url}`} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/50 mb-1 block">{at('tag')}</label>
                  <select value={bannerForm.tag} onChange={e => setBannerForm(f => ({...f, tag: e.target.value}))}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none"
                    data-testid="banner-tag-select">
                    <option value="">None</option>
                    <option value="NEW">NEW</option>
                    <option value="HOT">HOT</option>
                    <option value="FEATURED">FEATURED</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-1 block">{at('link')}</label>
                  <input value={bannerForm.link} onChange={e => setBannerForm(f => ({...f, link: e.target.value}))}
                    placeholder="/catalog or /packs"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setBannerModal(null)} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-sm transition-colors">{at('delete') === 'Eliminar' ? 'Cancelar' : 'Cancel'}</button>
                <button onClick={saveBanner} className="flex-1 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold transition-colors" data-testid="banner-save-btn">{at('save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'overview', label: at('overview'), icon: LayoutDashboard },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: unreadCount },
    { id: 'approvals', label: 'Aprobaciones', icon: FileCheck },
    { id: 'tracks', label: at('tracks'), icon: Music },
    { id: 'packs', label: at('packs'), icon: Package },
    { id: 'artists', label: 'Artists', icon: Star },
    { id: 'spotify-playlists', label: 'Spotify Playlists', icon: ListMusic },
    { id: 'orders', label: at('orders'), icon: ShoppingCart },
    { id: 'manual-sales', label: 'Ventas Manuales', icon: BookOpen },
    { id: 'deliveries', label: 'Deliveries', icon: Truck },
    { id: 'memberships', label: at('plans'), icon: Crown },
    { id: 'wallets', label: at('wallets'), icon: Wallet },
    { id: 'crypto', label: at('crypto'), icon: DollarSign },
    { id: 'users', label: at('users'), icon: Users },
    { id: 'leads', label: 'Leads', icon: UserPlus },
    { id: 'satisfaction', label: 'Satisfaction', icon: Star },
    { id: 'email-previews', label: 'Email Control', icon: Mail },
    { id: 'welcome-email', label: 'Welcome Email', icon: Mail },
    { id: 'campaigns', label: 'Campaigns', icon: Mail },
    { id: 'geo', label: at('geography'), icon: Globe },
    { id: 'content', label: at('content'), icon: FileText },
    { id: 'media', label: at('media'), icon: Image },
    { id: 'banners', label: at('banners'), icon: Layers },
    { id: 'support', label: at('support'), icon: MessageCircle, badge: unreadChatCount },
    { id: 'replacements', label: 'Reemplazos', icon: RefreshCw },
    { id: 'production', label: 'Produccion', icon: Music },
    { id: 'mastering', label: 'Mastering Clientes', icon: Music },
    { id: 'playlist-promotion', label: 'Playlist Promotion', icon: ListMusic, badge: needsAttentionPromos },
    { id: 'bulk-mastering', label: 'Bulk Mastering', icon: Upload },
    { id: 'ultimate', label: 'Ultimate Pack', icon: Crown },
    { id: 'news', label: 'Noticias', icon: Newspaper },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'licenses', label: 'Licenses', icon: FileCheck },
    { id: 'assistants', label: 'Asistentes', icon: Users },
    { id: 'ai', label: 'AI Assistant', icon: Sparkles },
    { id: 'settings', label: at('settings'), icon: Settings },
  ];

  // ═══════════ ORDER DETAIL MODAL ═══════════
  const OrderDetailModal = () => {
    if (!orderDetail && !orderDetailLoading) return null;
    const o = orderDetail;
    const isError = o?._error;
    const d = o?.delivery || {};
    const cr = o?.crypto;
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={closeOrderDetail} data-testid="order-detail-modal">
        <div className="bg-[#0c0c0f] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
          {orderDetailLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#E8720C]" />
              <span className="ml-3 text-white/50">Loading order...</span>
            </div>
          ) : isError ? (
            <div className="p-8 text-center">
              <XCircle className="w-10 h-10 text-red-400/60 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">Order Not Found</h3>
              <p className="text-sm text-white/50 mb-1">Could not load details for this order.</p>
              <p className="text-xs text-white/30 font-mono mb-4">{o.order_id}</p>
              <p className="text-xs text-red-400/60 mb-4">{o._error_msg}</p>
              <button onClick={closeOrderDetail} className="px-5 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-sm text-white/60">Close</button>
            </div>
          ) : o ? (
            <>
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div>
                  <h3 className="text-lg font-bold">Order Detail</h3>
                  <p className="text-xs text-white/40 font-mono mt-0.5">{o.order_id}</p>
                </div>
                <button onClick={closeOrderDetail} className="p-2 hover:bg-white/10 rounded-lg"><XCircle className="w-5 h-5 text-white/40" /></button>
              </div>
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ['Customer Name', o.customer_name, 'order-customer-name'],
                    ['Customer Email', o.customer_email, 'order-customer-email'],
                    ['Order ID', o.order_id, 'order-id-display', true],
                    ['Product', o.item_name, 'order-product'],
                    ['License Type', (o.license_type || 'basic').toUpperCase(), 'order-license'],
                    ['Amount', `$${(o.amount || 0).toFixed(2)}`, 'order-amount', false, true],
                    ['Payment Method', (o.payment_method || 'paypal').toUpperCase(), 'order-method'],
                    ['Date', o.created_at ? new Date(o.created_at).toLocaleString() : 'N/A', 'order-date'],
                  ].map(([label, value, testId, isMono, isHighlight]) => (
                    <div key={testId} data-testid={testId}>
                      <p className="text-[11px] text-white/40 mb-0.5">{label}</p>
                      <p className={`text-sm truncate ${isMono ? 'font-mono text-white/60' : ''} ${isHighlight ? 'text-[#E8720C] font-bold' : 'text-white/80'}`}>{value || 'N/A'}</p>
                    </div>
                  ))}
                  <div data-testid="order-status">
                    <p className="text-[11px] text-white/40 mb-0.5">Status</p>
                    <p className={`text-sm font-semibold ${o.payment_status === 'paid' ? 'text-green-400' : o.payment_status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{(o.payment_status || 'unknown').toUpperCase()}</p>
                  </div>
                  {o.paid_at && (
                    <div data-testid="order-paid-at">
                      <p className="text-[11px] text-white/40 mb-0.5">Paid At</p>
                      <p className="text-sm text-white/80">{new Date(o.paid_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>

                {/* Delivery Verification */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="delivery-verification">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2"><Truck className="w-4 h-4 text-[#E8720C]" />Delivery Verification</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      ['Delivered to Portal', d.delivered_to_portal, 'delivery-portal'],
                      ['License Generated', d.license_generated, 'delivery-license'],
                      ['MP3 Available', d.mp3_available, 'delivery-mp3'],
                      ['WAV Available', d.wav_available, 'delivery-wav'],
                    ].map(([label, ok, testId]) => (
                      <div key={testId} className="flex items-center gap-2" data-testid={testId}>
                        {ok ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400/50 flex-shrink-0" />}
                        <div>
                          <p className="text-[10px] text-white/40">{label}</p>
                          <p className={`text-xs font-semibold ${ok ? 'text-green-400' : 'text-red-400/60'}`}>{ok ? 'YES' : 'NO'}</p>
                        </div>
                      </div>
                    ))}
                    <div className="col-span-2 sm:col-span-1">
                      <p className="text-[10px] text-white/40 mb-0.5">Delivery Timestamp</p>
                      <p className="text-xs text-white/70" data-testid="delivery-timestamp">{d.sent_at ? new Date(d.sent_at).toLocaleString() : 'Not sent'}</p>
                    </div>
                  </div>
                </div>

                {/* Crypto section */}
                {cr && (
                  <div className="bg-yellow-500/5 border border-yellow-500/10 rounded-xl p-4" data-testid="crypto-section">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-yellow-400" />Crypto Payment</h4>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div><p className="text-[10px] text-white/40">Method</p><p className="text-xs text-white/80">{cr.method?.toUpperCase() || 'N/A'}</p></div>
                      <div><p className="text-[10px] text-white/40">Status</p><p className={`text-xs font-semibold ${cr.status === 'approved' ? 'text-green-400' : cr.status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{cr.status?.toUpperCase() || 'N/A'}</p></div>
                      {cr.wallet && <div className="col-span-2"><p className="text-[10px] text-white/40">Wallet</p><p className="text-xs font-mono text-white/60 break-all">{cr.wallet}</p></div>}
                      {cr.proof_value && <div className="col-span-2"><p className="text-[10px] text-white/40">Proof / TX Reference</p><p className="text-xs font-mono text-white/60 break-all">{cr.proof_value}</p></div>}
                      {cr.verified_at && <div><p className="text-[10px] text-white/40">Verified At</p><p className="text-xs text-white/80">{new Date(cr.verified_at).toLocaleString()}</p></div>}
                    </div>
                    {cr.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => handleApproveCrypto(o.order_id.replace('crypto_', ''), 'approved')}
                          className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="crypto-approve-btn">
                          <CheckCircle className="w-3.5 h-3.5" />Approve
                        </button>
                        <button onClick={() => handleApproveCrypto(o.order_id.replace('crypto_', ''), 'rejected')}
                          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="crypto-reject-btn">
                          <XCircle className="w-3.5 h-3.5" />Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                  {o.payment_status === 'paid' && (
                    <button onClick={() => handleSendDelivery(o.order_id)}
                      className="px-4 py-2 bg-[#E8720C]/20 hover:bg-[#E8720C]/30 text-[#E8720C] rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="order-send-delivery-btn">
                      <Mail className="w-3.5 h-3.5" />{d.delivered_to_portal ? 'Resend Delivery' : 'Send Delivery'}
                    </button>
                  )}
                  <button onClick={() => handleVerifyOrder(o.order_id)}
                    className="px-4 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="order-verify-btn">
                    <CheckCircle className="w-3.5 h-3.5" />Verify Order
                  </button>
                  {o.track_info && (
                    <button onClick={() => { setTab('tracks'); closeOrderDetail(); }}
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/60 rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="order-check-files-btn">
                      <FileText className="w-3.5 h-3.5" />Check Files
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  // ═══════════ RENDER EMAIL PREVIEWS ═══════════
  const renderEmailPreviews = () => {
    if (!emailPreviews.length) {
      return (
        <div>
          <h2 className="text-2xl font-bold mb-4">Email Previews</h2>
          <p className="text-white/50 text-sm mb-6">See exactly what your customers receive for each email type.</p>
          <button onClick={loadEmailPreviews} className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-semibold text-white" data-testid="load-email-previews-btn">
            Load Email Previews
          </button>
        </div>
      );
    }
    const active = emailPreviews.find(p => p.id === activeEmailPreview) || emailPreviews[0];
    return (
      <div>
        <h2 className="text-2xl font-bold mb-2">Email Previews</h2>
        <p className="text-white/50 text-sm mb-6">See exactly what your customers receive for each email type.</p>
        <div className="flex gap-2 mb-6">
          {emailPreviews.map(p => (
            <button key={p.id} onClick={() => setActiveEmailPreview(p.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeEmailPreview === p.id ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'}`}
              data-testid={`email-preview-tab-${p.id}`}>
              {p.name}
            </button>
          ))}
        </div>
        {active && (
          <div className="space-y-4">
            <div className="glass-card p-5 space-y-3" data-testid="email-preview-meta">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Sender</p>
                  <p className="text-sm text-white/80" data-testid="email-preview-sender">{active.sender}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-sm text-white/80 font-medium" data-testid="email-preview-subject">{active.subject}</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4" data-testid="email-preview-visual">
              <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3">Visual Preview</p>
              <div className="bg-[#050507] rounded-lg overflow-hidden border border-white/5">
                <iframe
                  srcDoc={active.html}
                  title={`Preview: ${active.name}`}
                  className="w-full border-0"
                  style={{ minHeight: '500px', background: '#050507' }}
                  sandbox="allow-same-origin"
                  data-testid="email-preview-iframe"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════ RENDER SALES SUMMARY ═══════════
  const renderSalesSummary = () => {
    if (!salesSummary) return null;
    const s = salesSummary;
    return (
      <div className="glass-card p-6 mb-6" data-testid="sales-summary">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#E8720C]" />Sales Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="sales-today">
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Today</p>
            <p className="text-2xl font-bold text-[#E8720C]">${s.today.revenue.toFixed(2)}</p>
            <p className="text-xs text-white/40 mt-1">{s.today.count} order{s.today.count !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="sales-week">
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">This Week</p>
            <p className="text-2xl font-bold text-[#E8720C]">${s.week.revenue.toFixed(2)}</p>
            <p className="text-xs text-white/40 mt-1">{s.week.count} order{s.week.count !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="sales-month">
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">This Month</p>
            <p className="text-2xl font-bold text-[#E8720C]">${s.month.revenue.toFixed(2)}</p>
            <p className="text-xs text-white/40 mt-1">{s.month.count} order{s.month.count !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white/[0.02] rounded-lg p-3">
            <p className="text-[10px] text-white/30">Paid Orders</p>
            <p className="text-lg font-bold text-green-400">{s.paid_orders}</p>
          </div>
          <div className="bg-white/[0.02] rounded-lg p-3">
            <p className="text-[10px] text-white/30">Pending Orders</p>
            <p className="text-lg font-bold text-yellow-400">{s.pending_orders}</p>
          </div>
          <div className="bg-white/[0.02] rounded-lg p-3">
            <p className="text-[10px] text-white/30">Crypto Orders</p>
            <p className="text-lg font-bold text-purple-400">{s.crypto_orders}{s.crypto_pending > 0 ? ` (${s.crypto_pending} pending)` : ''}</p>
          </div>
          <div className="bg-white/[0.02] rounded-lg p-3">
            <p className="text-[10px] text-white/30">Avg Order Value</p>
            <p className="text-lg font-bold text-white/80">${s.average_order_value.toFixed(2)}</p>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen pt-20" data-testid="admin-dashboard">
      <div className="flex">
        <aside className="w-16 lg:w-56 flex-shrink-0 border-r border-white/10 min-h-[calc(100vh-5rem)] p-3 lg:p-4 sticky top-20 flex flex-col">
          <div className="mb-6 hidden lg:block">
            <div className="flex items-center justify-between px-4">
              <p className="text-xs text-white/30 uppercase tracking-wider">{at('panel')}</p>
              <div className="flex gap-1">
                <button onClick={() => { setAdminLang('en'); localStorage.setItem('admin_lang','en'); }}
                  className={`px-2 py-0.5 text-[10px] rounded font-bold ${adminLang==='en' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                  data-testid="admin-lang-en">EN</button>
                <button onClick={() => { setAdminLang('es'); localStorage.setItem('admin_lang','es'); }}
                  className={`px-2 py-0.5 text-[10px] rounded font-bold ${adminLang==='es' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                  data-testid="admin-lang-es">ES</button>
              </div>
            </div>
          </div>
          <nav className="space-y-1">
            {tabs.map(t => (
              <div key={t.id} className="relative">
                <TabBtn icon={t.icon} label={t.label} active={tab===t.id} onClick={()=>setTab(t.id)} testId={`admin-tab-${t.id}`} />
                {t.badge > 0 && (
                  <span className="absolute top-1 right-1 lg:right-3 w-5 h-5 bg-[#E8720C] rounded-full flex items-center justify-center text-[10px] font-bold text-white">{t.badge}</span>
                )}
              </div>
            ))}
          </nav>
          <div className="mt-8 px-4 hidden lg:block">
            <button onClick={loadData} className="w-full flex items-center gap-2 text-xs text-white/40 hover:text-white/60 transition-colors" data-testid="admin-refresh"><RefreshCw className="w-3 h-3" />Refresh Data</button>
          </div>
          {/* Logout Button */}
          <div className="mt-auto px-4 py-4 border-t border-white/5">
            <button onClick={async () => {
              try { await axios.post(`${API_URL}/api/auth/logout`, {}, authHeaders()); } catch {}
              logout();
              window.location.href = '/login';
            }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Cerrar Sesion
            </button>
          </div>
        </aside>
        <main className="flex-1 p-4 lg:p-8 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-white/40" /></div>
          ) : (<>
            {tab === 'overview' && renderOverview()}
            {tab === 'tracks' && renderTracks()}
            {tab === 'packs' && renderPacks()}
            {tab === 'artists' && renderArtists()}
            {tab === 'spotify-playlists' && renderSpotifyPlaylists()}
            {tab === 'orders' && renderOrders()}
            {tab === 'deliveries' && renderDeliveries()}
            {tab === 'memberships' && renderMemberships()}
            {tab === 'wallets' && renderWallets()}
            {tab === 'crypto' && renderCrypto()}
            {tab === 'users' && renderUsers()}
            {tab === 'leads' && renderLeads()}
            {tab === 'satisfaction' && <SatisfactionPanel />}
            {tab === 'welcome-email' && <WelcomeEmailPanel />}
            {tab === 'campaigns' && (
              <div>
                <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl">
                  <button onClick={() => setCampaignSubTab('promotions')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${campaignSubTab === 'promotions' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                    <Tag className="w-3.5 h-3.5" /> Ofertas
                  </button>
                  <button onClick={() => setCampaignSubTab('marketing')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${campaignSubTab === 'marketing' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                    <Image className="w-3.5 h-3.5" /> Estudio Marketing
                  </button>
                  <button onClick={() => setCampaignSubTab('campaigns')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${campaignSubTab === 'campaigns' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                    <Mail className="w-3.5 h-3.5" /> Campanas Email
                  </button>
                </div>
                {campaignSubTab === 'promotions' && <PromotionsPanel />}
                {campaignSubTab === 'campaigns' && <CampaignsPanelV2 />}
                {campaignSubTab === 'marketing' && <MarketingStudio />}
              </div>
            )}
            {tab === 'geo' && renderGeo()}
            {tab === 'content' && renderContent()}
            {tab === 'media' && renderMedia()}
            {tab === 'banners' && renderBanners()}
            {tab === 'support' && <SupportPanel isAdmin={true} />}
            {tab === 'replacements' && <ReplacementPanel />}
            {tab === 'production' && <ProductionPanel isAdmin={true} />}
            {tab === 'mastering' && <MasteringStaffPanel />}
            {tab === 'playlist-promotion' && <PlaylistPromoAdminPanel />}
            {tab === 'bulk-mastering' && <BulkMasteringPanel userRole={user?.role} />}
            {tab === 'manual-sales' && <ManualSalesPanel />}
            {tab === 'ultimate' && <UltimateRequestsPanel />}
            {tab === 'news' && <NewsPanel />}
            {tab === 'faq' && renderFAQ()}
            {tab === 'licenses' && renderLicenses()}
            {tab === 'email-previews' && <EmailControlPanel />}
            {tab === 'assistants' && <AssistantsPanel />}
            {tab === 'approvals' && <ApprovalsPanel />}

            {/* ── NOTIFICATIONS MODULE ── */}
            {tab === 'notifications' && (
              <div data-testid="notifications-module">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Centro de Notificaciones</h2>
                  <div className="flex gap-2">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/50" data-testid="notif-mark-all-read">
                        Marcar todo leido
                      </button>
                    )}
                    <button onClick={loadData} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/50">
                      Actualizar
                    </button>
                  </div>
                </div>

                {/* ── Notification Detail Modal ── */}
                {selectedNotif && (
                  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setSelectedNotif(null); setBuyerHistory(null); }}>
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="admin-notif-detail-modal">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-white">{selectedNotif.title}</h3>
                        <button onClick={() => { setSelectedNotif(null); setBuyerHistory(null); }} className="p-1 rounded-lg hover:bg-white/10 text-white/40"><X className="w-5 h-5" /></button>
                      </div>
                      <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mb-4 ${
                        selectedNotif.type === 'playlist_submission' ? 'bg-purple-500/20 text-purple-400' :
                        selectedNotif.type === 'order_paid' ? 'bg-green-500/20 text-green-400' :
                        selectedNotif.type === 'chat_escalation' ? 'bg-yellow-500/20 text-yellow-400' :
                        selectedNotif.type === 'crypto' ? 'bg-[#E8720C]/20 text-[#E8720C]' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {selectedNotif.type === 'playlist_submission' ? 'Playlist' :
                         selectedNotif.type === 'order_paid' ? 'Orden' :
                         selectedNotif.type === 'chat_escalation' ? 'Soporte' :
                         selectedNotif.type || 'Notificacion'}
                      </div>
                      <div className="text-sm text-white/70 whitespace-pre-line mb-4">{selectedNotif.message}</div>
                      {selectedNotif.data && (
                        <div className="space-y-3 border-t border-white/10 pt-4">
                          {selectedNotif.data.user_name && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Cliente:</span>
                              <span className="text-sm text-white/80">{selectedNotif.data.user_name}</span>
                            </div>
                          )}
                          {selectedNotif.data.user_email && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Email:</span>
                              <span className="text-sm text-white/80">{selectedNotif.data.user_email}</span>
                            </div>
                          )}
                          {selectedNotif.data.item_name && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Track:</span>
                              <span className="text-sm text-[#E8720C]">{selectedNotif.data.item_name}</span>
                            </div>
                          )}
                          {selectedNotif.data.platform_name && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Plataforma:</span>
                              <span className="text-sm text-white/80">{selectedNotif.data.platform_name}</span>
                            </div>
                          )}
                          {selectedNotif.data.link && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Link:</span>
                              <a href={selectedNotif.data.link} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-[#E8720C] hover:underline truncate" data-testid="admin-notif-spotify-link">
                                {selectedNotif.data.link}
                              </a>
                            </div>
                          )}
                          {selectedNotif.data.client_note && (
                            <div className="mt-2 p-3 bg-white/[0.03] rounded-lg border-l-2 border-purple-500">
                              <div className="text-purple-400 text-[10px] font-semibold mb-1">Nota del Cliente:</div>
                              <div className="text-sm text-white/60">{selectedNotif.data.client_note}</div>
                            </div>
                          )}
                          {selectedNotif.data.count && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40 w-24">Cantidad:</span>
                              <span className="text-sm text-white/80">{selectedNotif.data.count} tracks</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Buyer History (Playlist notifications) ── */}
                      {(selectedNotif.type === 'playlist_submission') && (
                        <div className="mt-5 border-t border-white/10 pt-4" data-testid="buyer-history-section">
                          <h4 className="text-sm font-bold text-[#E8720C] mb-3">Historial del Cliente</h4>
                          {buyerHistoryLoading ? (
                            <div className="flex items-center gap-2 text-white/40 text-sm py-4">
                              <div className="w-4 h-4 border-2 border-[#E8720C] border-t-transparent rounded-full animate-spin" />
                              Cargando historial...
                            </div>
                          ) : buyerHistory ? (
                            <div className="space-y-4">
                              {/* Client Info */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase">Nombre</div>
                                  <div className="text-sm text-white/80">{buyerHistory.user?.name || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase">Email</div>
                                  <div className="text-sm text-white/80">{buyerHistory.user?.email || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase">Registrado</div>
                                  <div className="text-sm text-white/80">{buyerHistory.user?.created_at ? new Date(buyerHistory.user.created_at).toLocaleDateString() : 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase">Pais</div>
                                  <div className="text-sm text-white/80">{buyerHistory.user?.country || 'N/A'}</div>
                                </div>
                              </div>

                              {/* Summary */}
                              <div className="flex gap-4 p-3 bg-white/[0.03] rounded-xl">
                                <div className="text-center flex-1">
                                  <div className="text-xl font-bold text-[#E8720C]">{buyerHistory.total_orders}</div>
                                  <div className="text-[10px] text-white/40">Compras</div>
                                </div>
                                <div className="text-center flex-1">
                                  <div className="text-xl font-bold text-green-400">${buyerHistory.total_spent?.toFixed(2)}</div>
                                  <div className="text-[10px] text-white/40">Total Gastado</div>
                                </div>
                                <div className="text-center flex-1">
                                  <div className="text-xl font-bold text-purple-400">{buyerHistory.playlist_submissions?.length || 0}</div>
                                  <div className="text-[10px] text-white/40">Links Enviados</div>
                                </div>
                              </div>

                              {/* Purchase History */}
                              {buyerHistory.purchases?.length > 0 && (
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase mb-2">Historial de Compras</div>
                                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                    {buyerHistory.purchases.map((p, i) => (
                                      <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg text-xs">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="text-white/70 truncate">{p.item_name}</span>
                                          {p.includes_playlist && <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[9px] shrink-0">Playlist</span>}
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0 ml-2">
                                          <span className="text-white/40">{p.license_type || 'basic'}</span>
                                          <span className="text-green-400 font-medium">${p.amount?.toFixed(2)}</span>
                                          <span className="text-white/30">{p.date ? new Date(p.date).toLocaleDateString() : ''}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Previous Playlist Submissions */}
                              {buyerHistory.playlist_submissions?.length > 0 && (
                                <div>
                                  <div className="text-[10px] text-white/40 uppercase mb-2">Links de Playlist Enviados</div>
                                  <div className="space-y-1.5">
                                    {buyerHistory.playlist_submissions.map((s, i) => (
                                      <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg text-xs">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="text-white/70 truncate">{s.item_name}</span>
                                          <a href={s.platform_link} target="_blank" rel="noopener noreferrer" className="text-[#E8720C] hover:underline truncate text-[10px]">{s.platform_link}</a>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-2">
                                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.status === 'in_playlist' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                            {s.status === 'in_playlist' ? 'En Playlist' : 'Pendiente'}
                                          </span>
                                          <span className="text-white/30">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-white/30 text-sm">No se pudo cargar el historial</p>
                          )}
                        </div>
                      )}

                      <div className="text-[10px] text-white/20 mt-4">{selectedNotif.created_at ? new Date(selectedNotif.created_at).toLocaleString() : ''}</div>
                    </div>
                  </div>
                )}

                {notifications.length === 0 ? (
                  <div className="glass-card p-12 text-center">
                    <Bell className="w-10 h-10 text-white/10 mx-auto mb-4" />
                    <p className="text-white/30">Sin notificaciones</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map(n => {
                      const type = n.type || '';
                      const title = (n.title || '').toLowerCase();
                      // Color coding
                      let borderColor = 'border-white/10';
                      let bgColor = 'bg-white/[0.02]';
                      let badge = null;
                      
                      if (type === 'bulk_upload' || type === 'track_approval' || type === 'pack_approval' || title.includes('aprobacion') || title.includes('aprobación') || title.includes('approval') || title.includes('subida')) {
                        borderColor = 'border-red-500/30';
                        bgColor = 'bg-red-500/5';
                        badge = <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold uppercase">Pendiente Aprobacion</span>;
                      } else if (type === 'order_paid' || title.includes('paid') || title.includes('pagado') || title.includes('completad')) {
                        borderColor = 'border-green-500/30';
                        bgColor = 'bg-green-500/5';
                        badge = <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-[10px] font-bold uppercase">Orden Completada</span>;
                      } else if (type === 'chat_escalation' || title.includes('chat') || title.includes('soporte') || title.includes('escalad')) {
                        borderColor = 'border-yellow-500/30';
                        bgColor = 'bg-yellow-500/5';
                        badge = <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-[10px] font-bold uppercase">Soporte</span>;
                      } else if (type === 'playlist_submission' || title.includes('playlist')) {
                        borderColor = 'border-purple-500/30';
                        bgColor = 'bg-purple-500/5';
                        badge = <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-[10px] font-bold uppercase">Playlist</span>;
                      } else if (type === 'crypto' || title.includes('crypto')) {
                        borderColor = 'border-[#E8720C]/30';
                        bgColor = 'bg-[#E8720C]/5';
                        badge = <span className="px-2 py-1 bg-[#E8720C]/20 text-[#E8720C] rounded-lg text-[10px] font-bold uppercase">Crypto</span>;
                      }

                      return (
                        <div key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={`p-5 rounded-xl border-l-4 ${borderColor} ${bgColor} cursor-pointer hover:bg-white/[0.05] transition-all ${!n.read ? 'ring-1 ring-white/10' : ''}`}
                          data-testid={`notif-module-${n.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                {!n.read && <span className="w-2.5 h-2.5 bg-[#E8720C] rounded-full shrink-0 animate-pulse" />}
                                <h3 className="text-sm font-bold">{n.title}</h3>
                                {badge}
                              </div>
                              <p className="text-sm text-white/60 leading-relaxed">{n.message}</p>
                              {n.data && n.data.link && (
                                <p className="text-xs text-[#E8720C] mt-2 truncate">Link: {n.data.link}</p>
                              )}
                              {n.data && n.data.client_note && (
                                <p className="text-xs text-purple-400 mt-1 truncate">Nota: {n.data.client_note}</p>
                              )}
                              {n.data && n.data.count && (
                                <p className="text-xs text-white/40 mt-1">{n.data.count} tracks en lote</p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-[10px] text-white/30">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                              <p className="text-[10px] text-white/20">{n.created_at ? new Date(n.created_at).toLocaleTimeString() : ''}</p>
                              <ChevronRight className="w-4 h-4 text-white/20 mt-2 ml-auto" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── PRODUCTION APPROVALS INLINE ── */}
                <div id="prod-approvals-section" className="mt-8" data-testid="prod-approvals-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold">Aprobaciones de Produccion</h2>
                      {pendingProdApprovals.reduce((s, b) => s + b.total, 0) > 0 && (
                        <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-bold">
                          {pendingProdApprovals.reduce((s, b) => s + b.total, 0)} pendientes
                        </span>
                      )}
                    </div>
                    <button onClick={loadProdApprovals} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/50">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {pendingProdApprovals.length === 0 ? (
                    <div className="glass-card p-8 text-center">
                      <CheckCircle className="w-8 h-8 text-green-400/30 mx-auto mb-2" />
                      <p className="text-white/30 text-sm">No hay tracks pendientes de aprobacion</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingProdApprovals.map(batch => {
                        const allSelected = prodSelectAll[batch.batch_id] || false;
                        return (
                          <div key={batch.batch_id} className="glass-card border border-blue-500/20 overflow-hidden" data-testid={`prod-approval-batch-${batch.batch_id}`}>
                            <div className="p-4 bg-blue-500/5 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                                  <Music className="w-4 h-4 text-blue-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-white">Lote: {batch.batch_id}</p>
                                  <p className="text-[10px] text-white/30">{batch.total} tracks | {batch.sent_at ? new Date(batch.sent_at).toLocaleDateString() : ''}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors">
                                  <input type="checkbox" checked={allSelected}
                                    onChange={e => setProdSelectAll(prev => ({ ...prev, [batch.batch_id]: e.target.checked }))}
                                    className="rounded border-white/20" />
                                  <span className="text-[10px] text-white/60 font-medium">Seleccionar Todo</span>
                                </label>
                              </div>
                            </div>
                            <div className="p-4 space-y-1.5">
                              {batch.tracks?.map(t => (
                                <div key={t.id} className="flex items-center gap-3 p-2.5 bg-white/[0.02] rounded-lg hover:bg-white/[0.04] transition-colors">
                                  <Music className="w-3.5 h-3.5 text-blue-400/50 shrink-0" />
                                  <span className="text-xs text-white/70 flex-1 truncate font-medium">{t.title}</span>
                                  <span className="text-[10px] text-white/30">{t.genre}</span>
                                  <span className="text-[10px] text-[#E8720C] font-medium">{t.bpm} BPM</span>
                                  {t.price > 0 && <span className="text-xs text-green-400 font-bold">${t.price}</span>}
                                  <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-blue-500/15 text-blue-400">{t.license_type || 'basic'}</span>
                                </div>
                              ))}
                            </div>
                            <div className="p-4 border-t border-white/5 flex gap-2 justify-end">
                              <button onClick={() => rejectProdTracks(batch.tracks.map(t => t.id))}
                                disabled={prodApprovalLoading === 'reject'}
                                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                                data-testid="reject-prod-batch">
                                {prodApprovalLoading === 'reject' ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                Rechazar Lote
                              </button>
                              <button onClick={() => publishProdTracks(batch.tracks.map(t => t.id))}
                                disabled={prodApprovalLoading === 'publish'}
                                className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                                data-testid="approve-prod-batch">
                                {prodApprovalLoading === 'publish' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                Aprobar y Publicar ({batch.total} tracks)
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
            {tab === 'ai' && renderAI()}
            {tab === 'settings' && renderSettings()}
          </>)}
        </main>
      </div>

      {/* Track Modal */}
      {modal?.type === 'track' && (
        <TrackModal track={modal.data} tracks={tracks} mediaItems={mediaItems} onClose={() => setModal(null)} onSave={handleSaveTrack} onUpload={handleUploadFile} />
      )}
      {/* Pack Modal */}
      {modal?.type === 'pack' && (
        <PackModal pack={modal.data} tracks={tracks} mediaItems={mediaItems} onClose={() => setModal(null)} onSave={handleSavePack} onUpload={handleUploadFile} />
      )}
      {/* Order Detail Modal */}
      <OrderDetailModal />
    </div>
  );
};

// ═══════════ MEMBERSHIP EDITOR ═══════════
const MembershipEditor = ({ plan, onSave }) => {
  const [form, setForm] = useState({ ...plan, features: [...(plan.features || [])] });
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  const addFeature = () => { if (newFeature.trim()) { setForm({...form, features: [...form.features, newFeature.trim()]}); setNewFeature(''); } };
  const removeFeature = (i) => { setForm({...form, features: form.features.filter((_,idx)=>idx!==i)}); };

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...form, price: parseFloat(form.price), downloads_per_month: parseInt(form.downloads_per_month) });
    setSaving(false);
  };

  return (
    <div className="glass-card p-6" data-testid={`membership-editor-${plan.plan_id}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{plan.name}</h3>
        <span className="text-xs text-white/40 font-mono">{plan.plan_id}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div><label className="block text-xs text-white/50 mb-1">Plan Name</label>
          <input className="w-full input-glass" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid={`membership-name-${plan.plan_id}`} /></div>
        <div><label className="block text-xs text-white/50 mb-1">Price ($/month)</label>
          <input type="number" step="0.01" className="w-full input-glass" value={form.price} onChange={e=>setForm({...form, price: e.target.value})} data-testid={`membership-price-${plan.plan_id}`} /></div>
        <div><label className="block text-xs text-white/50 mb-1">Downloads/month (-1 = unlimited)</label>
          <input type="number" className="w-full input-glass" value={form.downloads_per_month} onChange={e=>setForm({...form, downloads_per_month: e.target.value})} data-testid={`membership-downloads-${plan.plan_id}`} /></div>
      </div>
      <div className="mb-4">
        <label className="block text-xs text-white/50 mb-2">Features / Benefits</label>
        <div className="space-y-2 mb-2">
          {form.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="flex-1 input-glass text-sm py-2" value={f} onChange={e => { const nf=[...form.features]; nf[i]=e.target.value; setForm({...form, features: nf}); }} />
              <button onClick={() => removeFeature(i)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="flex-1 input-glass text-sm py-2" placeholder="Add feature..." value={newFeature} onChange={e=>setNewFeature(e.target.value)} onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addFeature())} />
          <button onClick={addFeature} className="btn-secondary py-2 px-4 text-sm"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="btn-primary py-2 px-6 text-sm" data-testid={`membership-save-${plan.plan_id}`}>
        <Save className="w-4 h-4 inline mr-2" />{saving ? 'Saving...' : 'Save Plan'}
      </button>
    </div>
  );
};

// ═══════════ CONTENT EDITOR ═══════════
const ContentEditor = ({ section, label, current, onSave, fields, onUpload }) => {
  const [form, setForm] = useState({...current});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');

  const handleSave = async () => { setSaving(true); await onSave(section, form); setSaving(false); };

  const handleImageUpload = async (e, fieldKey) => {
    const file = e.target.files[0];
    if (!file || !onUpload) return;
    setUploading(fieldKey);
    try {
      const url = await onUpload(file, 'images');
      setForm({...form, [fieldKey]: url});
    } catch (err) { alert('Upload failed'); }
    setUploading('');
  };

  return (
    <div className="glass-card p-6 mb-6" data-testid={`content-editor-${section}`}>
      <h3 className="text-lg font-semibold mb-4">{label}</h3>
      <div className="space-y-4 mb-4">
        {fields.map(f => (
          <div key={f.key}>
            <label className="block text-xs text-white/50 mb-1">{f.label}</label>
            {f.type === 'toggle' ? (
              <button onClick={() => setForm({...form, [f.key]: !form[f.key]})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${form[f.key] ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                data-testid={`content-toggle-${section}-${f.key}`}>
                {form[f.key] ? 'Enabled' : 'Disabled'}
              </button>
            ) : f.type === 'textarea' ? (
              <textarea className="w-full input-glass min-h-[100px] resize-y" value={form[f.key]||''} onChange={e=>setForm({...form, [f.key]: e.target.value})} data-testid={`content-input-${section}-${f.key}`} />
            ) : f.type === 'image' ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input className="flex-1 input-glass text-sm" value={form[f.key]||''} onChange={e=>setForm({...form, [f.key]: e.target.value})} placeholder="Image URL or upload" data-testid={`content-input-${section}-${f.key}`} />
                  <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading===f.key?'opacity-50':''}`}>
                    <Upload className="w-4 h-4" />{uploading===f.key?'...':'Upload'}
                    <input type="file" accept="image/*" className="hidden" onChange={e=>handleImageUpload(e, f.key)} disabled={uploading===f.key} />
                  </label>
                </div>
                {form[f.key] && (
                  <div className="flex items-start gap-3">
                    <img src={form[f.key]} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-white/10" />
                    <button onClick={() => setForm({...form, [f.key]: ''})} className="text-xs text-red-400 hover:text-red-300 mt-1">Remove</button>
                  </div>
                )}
              </div>
            ) : (
              <input className="w-full input-glass" value={form[f.key]||''} onChange={e=>setForm({...form, [f.key]: e.target.value})} data-testid={`content-input-${section}-${f.key}`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={handleSave} disabled={saving} className="btn-primary py-2 px-6 text-sm" data-testid={`content-save-${section}`}>
        <Save className="w-4 h-4 inline mr-2" />{saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

// ═══════════ TRACK MODAL (with file upload) ═══════════
const TrackModal = ({ track, onClose, onSave, onUpload, mediaItems = [] }) => {
  const [form, setForm] = useState(track || {
    title:'', artist:'', genre:'', vibe:'', bpm:120, key:'C',
    duration:'3:00', price:5.99, cover_image:'', audio_preview_url:'', wav_file_url:'',
    description:'', is_premium:false, is_visible:true, includes_playlist:false, license_type:'basic', license_terms:''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [mp3Name, setMp3Name] = useState('');
  const [wavName, setWavName] = useState('');
  const [nameError, setNameError] = useState('');

  const getBaseName = (filename) => filename.replace(/\.[^/.]+$/, '').trim().toLowerCase();

  const validateNames = (newMp3, newWav) => {
    if (newMp3 && newWav && getBaseName(newMp3) !== getBaseName(newWav)) {
      setNameError(`Los nombres no coinciden: "${newMp3}" vs "${newWav}"`);
    } else {
      setNameError('');
    }
  };

  const handleFileUpload = async (e, type, targetField) => {
    const file = e.target.files[0];
    if (!file) return;
    // Track filename for validation
    if (targetField === 'audio_preview_url') {
      setMp3Name(file.name);
      validateNames(file.name, wavName);
    } else if (targetField === 'wav_file_url') {
      setWavName(file.name);
      validateNames(mp3Name, file.name);
    }
    setUploading(type + (targetField || ''));
    try {
      const url = await onUpload(file, type);
      const field = targetField || (type === 'audio' ? 'audio_preview_url' : 'cover_image');
      setForm({...form, [field]: url});
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.detail || err.message)); }
    setUploading('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await onSave({...form, bpm: parseInt(form.bpm), price: parseFloat(form.price)});
    } catch (err) { alert(err.response?.data?.detail || 'Error saving track'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="track-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{track ? 'Edit Track' : 'Add Track'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[{key:'title',label:'Title',required:true},{key:'artist',label:'Artist',required:true},{key:'genre',label:'Genre',required:true},{key:'vibe',label:'Vibe',required:true}].map(f=>(
            <div key={f.key}><label className="block text-xs text-white/50 mb-1">{f.label}</label>
              <input className="w-full input-glass" value={form[f.key]||''} onChange={e=>setForm({...form,[f.key]:e.target.value})} required={f.required} data-testid={`track-input-${f.key}`} /></div>
          ))}

          {/* Cover Image Upload */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Cover Image</label>
            <div className="flex gap-2">
              <input className="flex-1 input-glass text-sm" value={form.cover_image} onChange={e=>setForm({...form, cover_image: e.target.value})} placeholder="URL or upload file" data-testid="track-input-cover_image" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading==='images'?'opacity-50':''}`}>
                <Upload className="w-4 h-4" />{uploading==='images'?'...':'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={e=>handleFileUpload(e,'images')} disabled={uploading==='images'} />
              </label>
              {mediaItems.length > 0 && (
                <button type="button" onClick={() => setShowPicker(!showPicker)} className="btn-secondary py-2 px-3 text-sm flex items-center gap-1">
                  <Image className="w-4 h-4" />Media
                </button>
              )}
            </div>
            {showPicker && mediaItems.length > 0 && (
              <div className="mt-2 grid grid-cols-5 gap-2 max-h-32 overflow-y-auto p-2 bg-white/5 rounded-xl">
                {mediaItems.map(m => (
                  <button type="button" key={m.id} onClick={() => { setForm({...form, cover_image: m.url}); setShowPicker(false); }}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#E8720C]">
                    <img src={`${API_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {form.cover_image && <img src={form.cover_image.startsWith('/api') ? `${API_URL}${form.cover_image}` : form.cover_image} alt="" className="h-16 w-16 rounded-lg object-cover mt-2" />}
          </div>

          {/* Audio Upload - MP3 (Preview) */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Archivo MP3 *</label>
            <div className="flex gap-2 items-center">
              <input className="flex-1 input-glass text-sm" value={form.audio_preview_url} onChange={e=>setForm({...form, audio_preview_url: e.target.value})} placeholder="URL o sube archivo MP3" data-testid="track-input-audio_preview_url" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading==='audio'?'opacity-50':''}`}>
                <Upload className="w-4 h-4" />{uploading==='audio'?'...':'Subir MP3'}
                <input type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={e=>handleFileUpload(e,'audio','audio_preview_url')} disabled={uploading==='audio'} />
              </label>
            </div>
            {mp3Name && <p className="text-[10px] text-white/40 mt-1">{mp3Name}</p>}
          </div>

          {/* Audio Upload - WAV */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Archivo WAV *</label>
            <div className="flex gap-2 items-center">
              <input className="flex-1 input-glass text-sm" value={form.wav_file_url || ''} onChange={e=>setForm({...form, wav_file_url: e.target.value})} placeholder="URL o sube archivo WAV" data-testid="track-input-wav_file_url" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading==='audiowav_file_url'?'opacity-50':''}`}>
                <Upload className="w-4 h-4" />{uploading==='audiowav_file_url'?'...':'Subir WAV'}
                <input type="file" accept=".wav,audio/wav" className="hidden" onChange={e=>handleFileUpload(e,'audio','wav_file_url')} disabled={uploading==='audiowav_file_url'} />
              </label>
            </div>
            {wavName && <p className="text-[10px] text-white/40 mt-1">{wavName}</p>}
          </div>

          {/* File name validation */}
          {nameError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
              <p className="text-red-400 text-xs m-0">{nameError}</p>
            </div>
          )}
          {mp3Name && wavName && !nameError && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
              <p className="text-green-400 text-xs m-0">Nombres de archivos coinciden correctamente</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-xs text-white/50 mb-1">BPM</label><input type="number" className="w-full input-glass" value={form.bpm} onChange={e=>setForm({...form,bpm:e.target.value})} data-testid="track-input-bpm" /></div>
            <div><label className="block text-xs text-white/50 mb-1">Key</label><input className="w-full input-glass" value={form.key} onChange={e=>setForm({...form,key:e.target.value})} data-testid="track-input-key" /></div>
            <div><label className="block text-xs text-white/50 mb-1">Price ($)</label><input type="number" step="0.01" min="3" max="10" className="w-full input-glass" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} data-testid="track-input-price" /></div>
          </div>
          <div><label className="block text-xs text-white/50 mb-1">Duration</label><input className="w-full input-glass" value={form.duration} placeholder="3:24" onChange={e=>setForm({...form,duration:e.target.value})} data-testid="track-input-duration" /></div>
          <div><label className="block text-xs text-white/50 mb-1">Description</label><textarea className="w-full input-glass min-h-[60px]" value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} data-testid="track-input-description" /></div>

          {/* License Config */}
          <div>
            <label className="block text-xs text-white/50 mb-1">License Type</label>
            <select className="w-full input-glass" value={form.license_type || 'basic'} onChange={e => setForm({...form, license_type: e.target.value})} data-testid="track-input-license_type">
              <option value="basic">Basic License</option>
              <option value="exclusive">Exclusive License</option>
            </select>
          </div>
          <div><label className="block text-xs text-white/50 mb-1">Custom License Terms (optional)</label>
            <textarea className="w-full input-glass min-h-[50px] text-sm" value={form.license_terms||''} onChange={e=>setForm({...form,license_terms:e.target.value})} placeholder="Leave empty to use the default template terms" data-testid="track-input-license_terms" /></div>

          {/* Ecosistema de Playlists Toggle */}
          <div>
            <button type="button" data-testid="track-playlist-toggle" onClick={() => setForm({...form, includes_playlist: !form.includes_playlist})}
              className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-3 transition-colors"
              style={{
                background: form.includes_playlist ? 'rgba(232,114,12,0.15)' : 'rgba(255,255,255,0.03)',
                color: form.includes_playlist ? '#E8720C' : '#666',
                border: form.includes_playlist ? '1px solid rgba(232,114,12,0.3)' : '1px solid rgba(255,255,255,0.1)',
              }}>
              <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: 4, alignItems: 'center', justifyContent: 'center',
                background: form.includes_playlist ? '#E8720C' : '#333', color: '#fff', fontSize: 11 }}>
                {form.includes_playlist ? 'v' : ''}
              </span>
              <Music className="w-4 h-4" />
              Incluye Ecosistema de Playlists
            </button>
            {form.includes_playlist && (
              <p className="text-[10px] text-[#E8720C]/60 mt-1 text-center">El cliente podra enviar su link de Spotify para inclusion en playlists OG MUSIC</p>
            )}
          </div>

          {/* Premium + Visibility Toggles */}
          <div className="flex gap-4">
            <button type="button" onClick={()=>setForm({...form, is_premium: !form.is_premium})}
              className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_premium ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10'}`} data-testid="track-toggle-premium">
              <Crown className="w-4 h-4" />{form.is_premium ? 'Premium' : 'Basic'}
            </button>
            <button type="button" onClick={()=>setForm({...form, is_visible: !form.is_visible})}
              className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_visible ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`} data-testid="track-toggle-visible">
              {form.is_visible ? <><Eye className="w-4 h-4" />Visible</> : <><EyeOff className="w-4 h-4" />Hidden</>}
            </button>
          </div>

          <button type="submit" disabled={saving} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="track-form-save">
            <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Track'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ═══════════ PACK MODAL (with track assignment) ═══════════
const PackModal = ({ pack, tracks, onClose, onSave, onUpload, mediaItems = [] }) => {
  const [form, setForm] = useState(pack || {
    name:'', description:'', genre:'', mood:'', track_count:5,
    price:29.99, cover_image:'', formats:['WAV','MP3'], track_ids:[], artist:'', is_visible:true, includes_playlist:false
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const selectedTrackIds = form.track_ids || [];
  const availableTracks = tracks.filter(t => !selectedTrackIds.includes(t.id) && (
    !trackSearch || t.title.toLowerCase().includes(trackSearch.toLowerCase())
  ));

  const addTrack = (trackId) => setForm({...form, track_ids: [...selectedTrackIds, trackId], track_count: selectedTrackIds.length + 1});
  const removeTrack = (trackId) => setForm({...form, track_ids: selectedTrackIds.filter(id => id !== trackId), track_count: Math.max(0, selectedTrackIds.length - 1)});

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const url = await onUpload(file, 'images'); setForm({...form, cover_image: url}); }
    catch (err) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await onSave({...form, track_count: parseInt(form.track_count), price: parseFloat(form.price),
        formats: Array.isArray(form.formats) ? form.formats : form.formats.split(',').map(f=>f.trim())});
    } catch (err) { alert(err.response?.data?.detail || 'Error saving pack'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pack-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{pack ? 'Edit Pack' : 'Add Pack'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[{key:'name',label:'Pack Name',required:true},{key:'description',label:'Description',required:true},{key:'genre',label:'Genre',required:true},{key:'mood',label:'Mood',required:true},{key:'artist',label:'Artist (optional)'}].map(f=>(
            <div key={f.key}><label className="block text-xs text-white/50 mb-1">{f.label}</label>
              <input className="w-full input-glass" value={form[f.key]||''} onChange={e=>setForm({...form,[f.key]:e.target.value})} required={f.required} data-testid={`pack-input-${f.key}`} /></div>
          ))}

          {/* Cover Upload */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Cover Image</label>
            <div className="flex gap-2">
              <input className="flex-1 input-glass text-sm" value={form.cover_image} onChange={e=>setForm({...form, cover_image: e.target.value})} placeholder="URL or upload" data-testid="pack-input-cover_image" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading?'opacity-50':''}`}>
                <Upload className="w-4 h-4" />{uploading?'...':'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} disabled={uploading} />
              </label>
              {mediaItems.length > 0 && (
                <button type="button" onClick={() => setShowPicker(!showPicker)} className="btn-secondary py-2 px-3 text-sm flex items-center gap-1">
                  <Image className="w-4 h-4" />Media
                </button>
              )}
            </div>
            {showPicker && mediaItems.length > 0 && (
              <div className="mt-2 grid grid-cols-5 gap-2 max-h-32 overflow-y-auto p-2 bg-white/5 rounded-xl">
                {mediaItems.map(m => (
                  <button type="button" key={m.id} onClick={() => { setForm({...form, cover_image: m.url}); setShowPicker(false); }}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#E8720C]">
                    <img src={`${API_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {form.cover_image && <img src={form.cover_image.startsWith('/api') ? `${API_URL}${form.cover_image}` : form.cover_image} alt="" className="h-16 w-16 rounded-lg object-cover mt-2" />}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-white/50 mb-1">Tracks in Pack</label><input type="number" className="w-full input-glass" value={form.track_count} onChange={e=>setForm({...form,track_count:e.target.value})} data-testid="pack-input-track_count" /></div>
            <div><label className="block text-xs text-white/50 mb-1">Price ($)</label><input type="number" step="0.01" className="w-full input-glass" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} data-testid="pack-input-price" /></div>
          </div>

          {/* Track Assignment */}
          <div>
            <label className="block text-xs text-white/50 mb-2">Tracks en este Pack ({selectedTrackIds.length} seleccionados)</label>
            {selectedTrackIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {selectedTrackIds.map(tid => {
                  const tr = tracks.find(t => t.id === tid);
                  return tr ? (
                    <span key={tid} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E8720C]/15 border border-[#E8720C]/30 rounded-full text-xs text-[#E8720C]">
                      {tr.title} <button type="button" onClick={() => removeTrack(tid)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            <button type="button" onClick={() => setShowPicker(true)}
              className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-white/[0.05] border border-white/10 hover:bg-white/[0.08] transition-colors"
              data-testid="open-track-picker">
              <Plus className="w-4 h-4 text-[#E8720C]" /> Seleccionar Tracks
            </button>
          </div>

          {/* Full-screen Track Picker Modal */}
          {showPicker && (
            <div className="fixed inset-0 bg-black/95 z-[60] flex flex-col" data-testid="track-picker-modal"
              onKeyDown={e => { if (e.key === 'Escape') { setShowPicker(false); setTrackSearch(''); } }}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0a0a0e] shrink-0">
                <div className="flex items-center gap-4">
                  <button onClick={() => { setShowPicker(false); setTrackSearch(''); }}
                    className="p-2 rounded-lg hover:bg-white/10 text-white/50" data-testid="picker-back">
                    <X className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className="text-lg font-bold">Seleccionar Tracks para el Pack</h3>
                    <p className="text-xs text-[#E8720C]">{selectedTrackIds.length} seleccionados</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input className="input-glass text-sm w-72" placeholder="Buscar por nombre o artista..."
                    value={trackSearch} onChange={e => setTrackSearch(e.target.value)} data-testid="picker-search" autoFocus />
                  <button onClick={() => { setShowPicker(false); setTrackSearch(''); }}
                    className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-bold text-white"
                    data-testid="picker-done">
                    Listo ({selectedTrackIds.length})
                  </button>
                </div>
              </div>

              {/* Track List grouped by genre */}
              <div className="flex-1 overflow-y-auto">
                {(() => {
                  const filtered = tracks.filter(t => !trackSearch || t.title.toLowerCase().includes(trackSearch.toLowerCase()) || (t.artist||'').toLowerCase().includes(trackSearch.toLowerCase()));
                  const genres = {};
                  filtered.forEach(t => {
                    const g = t.genre || 'Sin Genero';
                    if (!genres[g]) genres[g] = [];
                    genres[g].push(t);
                  });
                  const sortedGenres = Object.keys(genres).sort();
                  return sortedGenres.map(genre => (
                    <div key={genre}>
                      <div className="sticky top-0 z-10 px-6 py-2.5 bg-[#111114] border-b border-white/[0.06] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-[#E8720C]">{genre}</span>
                          <span className="text-xs text-white/30">{genres[genre].length} tracks</span>
                        </div>
                        <button type="button" onClick={() => {
                          const genreIds = genres[genre].map(t => t.id);
                          const allSelected = genreIds.every(id => selectedTrackIds.includes(id));
                          if (allSelected) { genreIds.forEach(id => removeTrack(id)); }
                          else { genreIds.forEach(id => { if (!selectedTrackIds.includes(id)) addTrack(id); }); }
                        }} className="text-[10px] px-3 py-1 rounded-lg bg-white/[0.06] text-white/50 hover:bg-white/[0.1] hover:text-white/80 transition-colors">
                          {genres[genre].every(t => selectedTrackIds.includes(t.id)) ? 'Deseleccionar todo' : 'Seleccionar todo'}
                        </button>
                      </div>
                      {genres[genre].map((t, i) => {
                        const isSelected = selectedTrackIds.includes(t.id);
                        return (
                          <div key={t.id}
                            onClick={() => isSelected ? removeTrack(t.id) : addTrack(t.id)}
                            className={`flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-colors border-b border-white/[0.04] ${
                              isSelected ? 'bg-[#E8720C]/10' : i % 2 === 0 ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-white/[0.015] hover:bg-white/[0.04]'
                            }`}
                            data-testid={`picker-track-${t.id}`}>
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                              isSelected ? 'bg-[#E8720C] text-white' : 'border-2 border-white/20'
                            }`}>
                              {isSelected && <CheckCircle className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-medium ${isSelected ? 'text-[#E8720C]' : 'text-white/80'}`}>{t.title}</span>
                              <span className="text-sm text-white/30 ml-3">{t.artist}</span>
                            </div>
                            <span className="text-xs text-white/30 w-16 text-right shrink-0">{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                            <span className="text-xs text-[#E8720C] w-16 text-right shrink-0 font-medium">${t.price}</span>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Ecosistema Playlists */}
          <button type="button" onClick={() => setForm({...form, includes_playlist: !form.includes_playlist})}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-3 transition-colors ${
              form.includes_playlist ? 'bg-[#E8720C]/15 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/[0.03] text-white/40 border border-white/10'
            }`} data-testid="pack-playlist-toggle">
            <span className={`inline-flex w-5 h-5 rounded items-center justify-center text-xs font-bold ${form.includes_playlist ? 'bg-[#E8720C] text-white' : 'bg-white/10 text-white/30'}`}>
              {form.includes_playlist ? 'v' : ''}
            </span>
            <Music className="w-4 h-4" />
            Incluye Ecosistema de Playlists
          </button>
          {form.includes_playlist && (
            <p className="text-[10px] text-[#E8720C]/60 text-center">El cliente podra enviar su link de Spotify para inclusion en playlists OG MUSIC</p>
          )}

          {/* Visibility */}
          <button type="button" onClick={()=>setForm({...form, is_visible: !form.is_visible})}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_visible ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
            {form.is_visible ? <><Eye className="w-4 h-4" />Visible on Store</> : <><EyeOff className="w-4 h-4" />Hidden from Store</>}
          </button>

          <button type="submit" disabled={saving} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="pack-form-save">
            <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Pack'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ═══════════ ARTIST MODAL ═══════════
const ArtistModal = ({ artist, onClose, onSave, onUpload }) => {
  const [form, setForm] = useState(artist || {
    name: '', image: '', spotify_url: '', youtube_url: '', genre: '', tag: '', is_featured: true, order: 0
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUpload(file, 'images');
      setForm({ ...form, image: url });
    } catch (err) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Artist name is required'); return; }
    setSaving(true);
    try { await onSave({ ...form, order: parseInt(form.order) || 0 }); }
    catch (err) { alert('Error saving'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="artist-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold">{artist ? 'Edit Artist' : 'Add Artist'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Artist Image</label>
            <div className="flex gap-2">
              <input className="flex-1 input-glass text-sm" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="Image URL or upload" data-testid="artist-input-image" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading ? 'opacity-50' : ''}`}>
                <Upload className="w-4 h-4" />{uploading ? '...' : 'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
            </div>
            {form.image && (
              <div className="flex items-start gap-3 mt-2">
                <img src={form.image} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                <button type="button" onClick={() => setForm({ ...form, image: '' })} className="text-xs text-red-400 hover:text-red-300 mt-1">Remove</button>
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Artist Name *</label>
            <input className="w-full input-glass" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. ISKARA" data-testid="artist-input-name" required />
          </div>

          {/* Genre */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Genre</label>
            <input className="w-full input-glass" value={form.genre} onChange={e => setForm({ ...form, genre: e.target.value })} placeholder="e.g. Latin Pop" data-testid="artist-input-genre" />
          </div>

          {/* Tag */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Tag / Label</label>
            <input className="w-full input-glass" value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} placeholder="e.g. Live on all platforms" data-testid="artist-input-tag" />
          </div>

          {/* Spotify */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Spotify Link (Perfil del Artista)</label>
            <input className="w-full input-glass" value={form.spotify_url} onChange={e => setForm({ ...form, spotify_url: e.target.value })} placeholder="https://open.spotify.com/artist/16AVQwtbLBtxeVfRIgLtV0" data-testid="artist-input-spotify" />
            <p className="text-[10px] text-white/30 mt-1">Perfil del artista → Compartir → Copiar enlace. Ejemplo: https://open.spotify.com/artist/16AVQwtbLBtxeVfRIgLtV0</p>
          </div>

          {/* Spotify Playlist */}
          <div>
            <label className="block text-xs text-white/50 mb-1">Spotify Playlist (del Artista)</label>
            <input className="w-full input-glass" value={form.spotify_playlist_url || ''} onChange={e => setForm({ ...form, spotify_playlist_url: e.target.value })} placeholder="https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ" data-testid="artist-input-spotify-playlist" />
            <p className="text-[10px] text-white/30 mt-1">Playlist del artista → Compartir → Copiar enlace. Ejemplo: https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ</p>
          </div>

          {/* YouTube */}
          <div>
            <label className="block text-xs text-white/50 mb-1">YouTube Link (optional)</label>
            <input className="w-full input-glass" value={form.youtube_url} onChange={e => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/@..." data-testid="artist-input-youtube" />
          </div>

          {/* Order + Featured */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-white/50 mb-1">Display Order</label>
              <input type="number" className="w-full input-glass" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} data-testid="artist-input-order" />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">Featured</label>
              <button type="button" onClick={() => setForm({ ...form, is_featured: !form.is_featured })}
                className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${form.is_featured ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10'}`}
                data-testid="artist-input-featured">
                {form.is_featured ? 'Featured (visible on homepage)' : 'Hidden from homepage'}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 btn-primary py-2.5 text-sm" data-testid="artist-save-btn">
              <Save className="w-4 h-4 inline mr-1" />{saving ? 'Saving...' : (artist ? 'Update Artist' : 'Add Artist')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary py-2.5 px-6 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPage;

// ═══════════ DISTRIBUTION GUIDE PANEL (Ultimate Pack exclusive) ═══════════
const DistributionGuidePanel = () => {
  const [info, setInfo] = useState({ exists: false });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/distribution-guide`, { withCredentials: true });
      setInfo(r.data || { exists: false });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo PDF permitido');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/admin/distribution-guide/upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success('Guía actualizada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo PDF');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const sizeKb = info.size_bytes ? Math.round(info.size_bytes / 1024) : 0;
  return (
    <div className="glass-card p-6" data-testid="settings-distribution-guide">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-[#E8720C]" />
        </div>
        <div>
          <h3 className="font-semibold">Guía de Distribución (Ultimate Pack)</h3>
          <p className="text-xs text-white/40">PDF privado · Solo visible para clientes que compraron el Ultimate Pack</p>
        </div>
      </div>

      {info.exists ? (
        <div className="p-4 bg-green-500/5 border border-green-400/20 rounded-lg mb-4">
          <p className="text-sm font-medium text-green-400 mb-1">✓ Guía publicada</p>
          <p className="text-xs text-white/60 mb-1">Archivo: <span className="text-white/80">{info.filename}</span></p>
          <p className="text-xs text-white/60 mb-1">Tamaño: <span className="text-white/80">{sizeKb} KB</span></p>
          <p className="text-xs text-white/60">Subido: <span className="text-white/80">{new Date(info.uploaded_at).toLocaleString()}</span></p>
        </div>
      ) : (
        <div className="p-4 bg-white/[0.02] border border-white/10 rounded-lg mb-4">
          <p className="text-sm text-white/50">Aún no hay guía publicada. Sube un PDF para activar el beneficio.</p>
        </div>
      )}

      <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={handleFile} className="hidden" data-testid="guide-file-input" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="guide-upload-btn"
      >
        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo…</> : <>📄 {info.exists ? 'Reemplazar PDF' : 'Subir PDF'}</>}
      </button>
    </div>
  );
};

// ═══════════ FAQ MODAL ═══════════
const FAQModal = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState({ question: item?.question || '', answer: item?.answer || '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({ ...item, ...form });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="glass-card p-6 w-full max-w-lg" data-testid="faq-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{item?.id ? 'Edit Question' : 'Add Question'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Question</label>
            <input className="w-full input-glass" value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} required data-testid="faq-input-question" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Answer</label>
            <textarea className="w-full input-glass min-h-[120px] resize-y" value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} required data-testid="faq-input-answer" />
          </div>
          <button type="submit" disabled={saving} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="faq-save-btn">
            <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ═══════════ LICENSE TEMPLATE EDITOR ═══════════
const LicenseTemplateEditor = ({ template, onSave, onPreview }) => {
  const defaultTemplate = {
    company_name: 'OG MUSIC GROUP',
    company_email: 'support@ogmusicgroup.com',
    company_website: 'ogmusicgroup.com',
    certificate_title: 'OFFICIAL LICENSE CERTIFICATE',
    basic_terms: [],
    exclusive_terms: [],
    footer_text: 'This document serves as proof of purchase and license agreement.'
  };
  const [form, setForm] = useState({ ...defaultTemplate, ...template });
  const [saving, setSaving] = useState(false);
  const [newBasicTerm, setNewBasicTerm] = useState('');
  const [newExclusiveTerm, setNewExclusiveTerm] = useState('');

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const addTerm = (type) => {
    const key = type === 'basic' ? 'basic_terms' : 'exclusive_terms';
    const val = type === 'basic' ? newBasicTerm : newExclusiveTerm;
    if (!val.trim()) return;
    setForm({ ...form, [key]: [...(form[key] || []), val.trim()] });
    if (type === 'basic') setNewBasicTerm('');
    else setNewExclusiveTerm('');
  };

  const removeTerm = (type, index) => {
    const key = type === 'basic' ? 'basic_terms' : 'exclusive_terms';
    setForm({ ...form, [key]: form[key].filter((_, i) => i !== index) });
  };

  const updateTerm = (type, index, value) => {
    const key = type === 'basic' ? 'basic_terms' : 'exclusive_terms';
    const newTerms = [...form[key]];
    newTerms[index] = value;
    setForm({ ...form, [key]: newTerms });
  };

  return (
    <div className="space-y-6" data-testid="license-template-editor">
      {/* Company Info */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Company Name</label>
            <input className="w-full input-glass" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="license-company-name" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Certificate Title</label>
            <input className="w-full input-glass" value={form.certificate_title} onChange={e => setForm({ ...form, certificate_title: e.target.value })} data-testid="license-cert-title" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Support Email</label>
            <input className="w-full input-glass" value={form.company_email} onChange={e => setForm({ ...form, company_email: e.target.value })} data-testid="license-company-email" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Website</label>
            <input className="w-full input-glass" value={form.company_website} onChange={e => setForm({ ...form, company_website: e.target.value })} data-testid="license-company-website" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs text-white/50 mb-1">Footer Text</label>
          <input className="w-full input-glass" value={form.footer_text} onChange={e => setForm({ ...form, footer_text: e.target.value })} data-testid="license-footer-text" />
        </div>
      </div>

      {/* Basic License Terms */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Basic License Terms</h3>
          <button onClick={() => onPreview('basic')} className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1" data-testid="license-preview-basic">
            <Eye className="w-3 h-3" />Preview PDF
          </button>
        </div>
        <div className="space-y-2 mb-3">
          {(form.basic_terms || []).map((term, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-white/30 w-6 shrink-0">{i + 1}.</span>
              <input className="flex-1 input-glass text-sm py-2" value={term} onChange={e => updateTerm('basic', i, e.target.value)} />
              <button onClick={() => removeTerm('basic', i)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="flex-1 input-glass text-sm py-2" placeholder="Add new term..." value={newBasicTerm} onChange={e => setNewBasicTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTerm('basic'))} />
          <button onClick={() => addTerm('basic')} className="btn-secondary py-2 px-4 text-sm"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Exclusive License Terms */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Exclusive License Terms</h3>
          <button onClick={() => onPreview('exclusive')} className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1" data-testid="license-preview-exclusive">
            <Eye className="w-3 h-3" />Preview PDF
          </button>
        </div>
        <div className="space-y-2 mb-3">
          {(form.exclusive_terms || []).map((term, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-white/30 w-6 shrink-0">{i + 1}.</span>
              <input className="flex-1 input-glass text-sm py-2" value={term} onChange={e => updateTerm('exclusive', i, e.target.value)} />
              <button onClick={() => removeTerm('exclusive', i)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="flex-1 input-glass text-sm py-2" placeholder="Add new term..." value={newExclusiveTerm} onChange={e => setNewExclusiveTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTerm('exclusive'))} />
          <button onClick={() => addTerm('exclusive')} className="btn-secondary py-2 px-4 text-sm"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving} className="btn-primary py-3 px-8 flex items-center gap-2" data-testid="license-template-save">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Template'}
      </button>
    </div>
  );
};


// ═══════════ AI SETTINGS EDITOR ═══════════
const SatisfactionPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/satisfaction-stats`, { withCredentials: true });
        setData(r.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch_();
  }, []);

  if (loading) return <p className="text-white/40">Loading satisfaction data...</p>;
  if (!data) return <p className="text-white/40">Failed to load data.</p>;

  const stats = data.stats || {};
  const ratings = (data.ratings || []).filter(r => {
    if (filter === 'low') return r.rating <= 5;
    if (filter === 'high') return r.rating > 5;
    if (filter === 'free') return r.ai_mode === 'free';
    if (filter === 'paid') return r.ai_mode === 'paid';
    return true;
  });

  const dist = stats.distribution || {};

  return (
    <div data-testid="admin-satisfaction-section">
      <h2 className="text-2xl font-bold mb-6">Chat Satisfaction</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="glass-card p-4 text-center" data-testid="satisfaction-avg">
          <p className={`text-3xl font-bold ${stats.average >= 7 ? 'text-green-400' : stats.average >= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
            {stats.average || 0}
          </p>
          <p className="text-xs text-white/50">Average Rating</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-3xl font-bold">{stats.total || 0}</p>
          <p className="text-xs text-white/50">Total Ratings</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-3xl font-bold text-red-400">{stats.low_count || 0}</p>
          <p className="text-xs text-white/50">Low (1-5)</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-xs text-white/40 mb-1">By AI Mode</p>
          <p className="text-sm"><span className="text-green-400">Free: {stats.free_avg || 0}</span> / <span className="text-orange-400">Paid: {stats.paid_avg || 0}</span></p>
        </div>
      </div>

      {/* Distribution Chart */}
      {stats.total > 0 && (
        <div className="glass-card p-4 mb-6">
          <p className="text-xs text-white/40 mb-3">Rating Distribution</p>
          <div className="flex items-end gap-1 h-24">
            {[1,2,3,4,5,6,7,8,9,10].map(n => {
              const count = dist[String(n)] || 0;
              const maxCount = Math.max(...Object.values(dist).map(Number), 1);
              const pct = (count / maxCount) * 100;
              return (
                <div key={n} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full rounded-t transition-all ${n <= 5 ? 'bg-red-500/60' : 'bg-green-500/60'}`} style={{ height: `${Math.max(pct, 4)}%` }} />
                  <span className="text-[9px] text-white/30">{n}</span>
                  <span className="text-[9px] text-white/50">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {['all', 'low', 'high', 'free', 'paid'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            data-testid={`satisfaction-filter-${f}`}
          >
            {f === 'all' ? 'All' : f === 'low' ? 'Low (1-5)' : f === 'high' ? 'High (6-10)' : f === 'free' ? 'Free AI' : 'Paid AI'}
          </button>
        ))}
      </div>

      {/* Feedback List */}
      <div className="space-y-2">
        {ratings.length === 0 ? (
          <p className="text-white/30 text-sm text-center py-8">No ratings yet. Ratings will appear after users end chat sessions.</p>
        ) : ratings.map((r, i) => (
          <div key={i} className={`glass-card p-4 flex items-start gap-4 ${r.rating <= 5 ? 'border-l-2 border-red-500' : ''}`} data-testid={`satisfaction-item-${i}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg shrink-0 ${
              r.rating <= 5 ? 'bg-red-500/15 text-red-400' : r.rating <= 7 ? 'bg-yellow-500/15 text-yellow-400' : 'bg-green-500/15 text-green-400'
            }`}>
              {r.rating}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs text-white/40 mb-1">
                <span>{r.name || 'Anonymous'}</span>
                {r.email && <span className="text-white/20">({r.email})</span>}
                <span className={`px-1.5 py-0.5 rounded text-[9px] ${r.ai_mode === 'paid' ? 'bg-orange-500/15 text-orange-400' : 'bg-green-500/15 text-green-400'}`}>
                  {r.ai_mode || 'unknown'}
                </span>
                <span className="ml-auto">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
              </div>
              {r.comment && <p className="text-sm text-white/60">{r.comment}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const AISettingsEditor = ({ settings, onSave }) => {
  const defaultSettings = {
    enabled: true,
    ai_mode: 'hybrid',
    free_ai_enabled: true,
    paid_ai_enabled: true,
    model: 'gpt-5.2',
    openai_api_key: '',
    tone: '',
    sales_script: '',
    welcome_message: 'Hola, soy el asistente de OG MUSIC. Dime qué tipo de música necesitas y tu presupuesto, y te recomiendo lo mejor para ti.'
  };
  const [form, setForm] = useState({ ...defaultSettings, ...settings });
  const [saving, setSaving] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [usageStats, setUsageStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/ai-usage-stats`, { withCredentials: true });
        setUsageStats(r.data);
      } catch (e) { console.error('AI stats error', e); }
      setLoadingStats(false);
    };
    fetchStats();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...form };
    if (newApiKey.trim()) {
      payload.openai_api_key = newApiKey.trim();
    } else {
      delete payload.openai_api_key;
    }
    delete payload.openai_api_key_masked;
    delete payload.has_openai_key;
    await onSave(payload);
    setShowKeyInput(false);
    setNewApiKey('');
    setSaving(false);
  };

  const showPaidConfig = form.ai_mode !== 'free_only';
  const modeDescriptions = {
    hybrid: 'Free AI handles simple messages (greetings, FAQs, navigation). Complex questions escalate to Paid AI automatically.',
    free_only: 'Only rule-based responses. Zero API cost. Best for basic support and navigation.',
    paid_only: 'Every message goes to OpenAI. Highest quality but highest cost.',
  };

  return (
    <div className="space-y-6" data-testid="ai-settings-editor">
      {/* Enable/Disable */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">AI Assistant Status</h3>
            <p className="text-sm text-white/50">Enable or disable the AI assistant across the entire platform.</p>
          </div>
          <button
            onClick={() => setForm({ ...form, enabled: !form.enabled })}
            className={`px-6 py-2 rounded-xl font-semibold text-sm transition-all ${form.enabled ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}
            data-testid="ai-toggle-enabled"
          >
            {form.enabled ? 'ENABLED' : 'DISABLED'}
          </button>
        </div>
      </div>

      {/* AI Mode Selector — 3 modes */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-2">AI Mode</h3>
        <p className="text-xs text-white/40 mb-4">Select how the AI routes messages. Hybrid mode reduces costs while keeping quality high.</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'hybrid', label: 'Hybrid', icon: '⚡', desc: 'Free AI first, Paid fallback' },
            { id: 'free_only', label: 'Free Only', icon: '💰', desc: 'Zero API cost' },
            { id: 'paid_only', label: 'Paid Only', icon: '🧠', desc: 'Full OpenAI power' },
          ].map(mode => (
            <button
              key={mode.id}
              onClick={() => setForm({ ...form, ai_mode: mode.id })}
              className={`p-4 rounded-xl border text-left transition-all ${form.ai_mode === mode.id ? 'border-[#E8720C] bg-[#E8720C]/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
              data-testid={`ai-mode-${mode.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-3 h-3 rounded-full ${form.ai_mode === mode.id ? 'bg-[#E8720C]' : 'bg-white/20'}`} />
                <span className="font-semibold text-sm">{mode.label}</span>
              </div>
              <p className="text-xs text-white/40 mt-1">{mode.desc}</p>
            </button>
          ))}
        </div>
        <div className="mt-3 p-3 bg-[#E8720C]/5 border border-[#E8720C]/20 rounded-xl">
          <p className="text-xs text-[#E8720C]">{modeDescriptions[form.ai_mode]}</p>
        </div>
      </div>

      {/* Individual Toggles */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">AI Engine Toggles</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/5">
            <div>
              <p className="text-sm font-medium">Free AI</p>
              <p className="text-xs text-white/40">Rule-based, instant, no cost</p>
            </div>
            <button
              onClick={() => setForm({ ...form, free_ai_enabled: !form.free_ai_enabled })}
              className={`w-12 h-6 rounded-full transition-all relative ${form.free_ai_enabled ? 'bg-green-500' : 'bg-white/20'}`}
              data-testid="ai-toggle-free"
            >
              <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${form.free_ai_enabled ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/5">
            <div>
              <p className="text-sm font-medium">Paid AI</p>
              <p className="text-xs text-white/40">OpenAI GPT, advanced reasoning</p>
            </div>
            <button
              onClick={() => setForm({ ...form, paid_ai_enabled: !form.paid_ai_enabled })}
              className={`w-12 h-6 rounded-full transition-all relative ${form.paid_ai_enabled ? 'bg-green-500' : 'bg-white/20'}`}
              data-testid="ai-toggle-paid"
            >
              <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${form.paid_ai_enabled ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* AI Usage Analytics */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">AI Usage Analytics</h3>
        {loadingStats ? (
          <p className="text-sm text-white/40">Loading stats...</p>
        ) : usageStats ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                <p className="text-2xl font-bold text-green-400">{usageStats.total?.free || 0}</p>
                <p className="text-xs text-white/50">Free AI Requests</p>
              </div>
              <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                <p className="text-2xl font-bold text-orange-400">{usageStats.total?.paid || 0}</p>
                <p className="text-xs text-white/50">Paid AI Requests</p>
              </div>
              <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-2xl font-bold text-yellow-400">{usageStats.total?.escalated || 0}</p>
                <p className="text-xs text-white/50">Escalations</p>
              </div>
              <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-2xl font-bold text-cyan-400">{usageStats.cost_savings_pct || 0}%</p>
                <p className="text-xs text-white/50">Cost Savings</p>
              </div>
            </div>

            {/* Detail Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/40 mb-1">Escalation Rate</p>
                <p className="text-lg font-semibold">{usageStats.escalation_rate || 0}%</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/40 mb-1">Est. Paid Cost</p>
                <p className="text-lg font-semibold">${usageStats.estimated_cost_usd || '0.00'}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/40 mb-1">Avg Response Time</p>
                <p className="text-lg font-semibold">
                  <span className="text-green-400">{usageStats.avg_response_time_ms?.free || 0}ms</span>
                  <span className="text-white/30 mx-1">/</span>
                  <span className="text-orange-400">{usageStats.avg_response_time_ms?.paid || 0}ms</span>
                </p>
                <p className="text-[10px] text-white/30">Free / Paid</p>
              </div>
            </div>

            {/* Today Stats */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs text-white/40 mb-2">Today</p>
              <div className="flex gap-4 text-sm">
                <span className="text-green-400">Free: {usageStats.today?.free || 0}</span>
                <span className="text-orange-400">Paid: {usageStats.today?.paid || 0}</span>
                <span className="text-yellow-400">Escalated: {usageStats.today?.escalated || 0}</span>
              </div>
            </div>

            {/* Daily Breakdown */}
            {usageStats.daily_breakdown?.length > 0 && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/40 mb-2">Last 7 Days</p>
                <div className="space-y-1">
                  {usageStats.daily_breakdown.map((day, i) => {
                    const total = (day.free || 0) + (day.paid || 0);
                    const freePct = total > 0 ? Math.round((day.free || 0) / total * 100) : 0;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-white/40 w-20 shrink-0">{day.date?.slice(5)}</span>
                        <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden flex">
                          {freePct > 0 && <div className="bg-green-500/60 h-full" style={{ width: `${freePct}%` }} />}
                          {freePct < 100 && <div className="bg-orange-500/60 h-full" style={{ width: `${100 - freePct}%` }} />}
                        </div>
                        <span className="text-white/50 w-24 text-right shrink-0">{day.free || 0}F / {day.paid || 0}P</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-white/30">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-500/60" /> Free</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-500/60" /> Paid</span>
                </div>
              </div>
            )}

            {/* Top Intents */}
            {usageStats.top_intents?.length > 0 && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/40 mb-2">Top Intents</p>
                <div className="flex flex-wrap gap-2">
                  {usageStats.top_intents.map((item, i) => (
                    <span key={i} className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-xs">
                      {item.intent} <span className="text-white/40">({item.count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-white/40">No usage data yet. Analytics will appear once users start chatting.</p>
        )}
      </div>

      {/* OpenAI API Key — show when paid AI is relevant */}
      {showPaidConfig && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-2">OpenAI API Key</h3>
          <p className="text-xs text-white/40 mb-4">Your API key is stored securely. If no key is provided, the system uses the default fallback key.</p>
          <div className="flex items-center gap-3">
            {settings?.has_openai_key ? (
              <div className="flex-1 flex items-center gap-3">
                <div className="flex-1 px-4 py-3 bg-green-500/5 border border-green-500/20 rounded-xl text-sm text-green-400 font-mono">
                  {settings.openai_api_key_masked || 'Key configured'}
                </div>
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
              </div>
            ) : (
              <div className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white/40">
                No key configured (using default fallback)
              </div>
            )}
            <button
              onClick={() => setShowKeyInput(!showKeyInput)}
              className="btn-secondary py-2.5 px-4 text-xs"
              data-testid="ai-change-key-btn"
            >
              {settings?.has_openai_key ? 'Change Key' : 'Add Key'}
            </button>
          </div>
          {showKeyInput && (
            <div className="mt-3 flex gap-2">
              <input
                type="password"
                value={newApiKey}
                onChange={e => setNewApiKey(e.target.value)}
                placeholder="sk-..."
                className="flex-1 input-glass text-sm font-mono"
                data-testid="ai-api-key-input"
              />
              <button
                onClick={() => { setShowKeyInput(false); setNewApiKey(''); }}
                className="px-3 py-2 text-xs text-white/40 hover:text-white"
              >Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* Model & Welcome */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">{showPaidConfig ? 'Model & Greeting' : 'Greeting'}</h3>
        <div className={`grid grid-cols-1 ${showPaidConfig ? 'md:grid-cols-2' : ''} gap-4`}>
          {showPaidConfig && (
            <div>
              <label className="block text-xs text-white/50 mb-1">AI Model</label>
              <select className="w-full input-glass" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} data-testid="ai-model-select">
                <option value="gpt-5.2">GPT-5.2 (Recommended)</option>
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4o-mini">GPT-4o Mini</option>
              </select>
            </div>
          )}
          {showPaidConfig && (
            <div>
              <label className="block text-xs text-white/50 mb-1">Tone / Personality</label>
              <input className="w-full input-glass" value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} placeholder="e.g., Confident, persuasive, friendly" data-testid="ai-tone-input" />
            </div>
          )}
        </div>
        <div className="mt-4">
          <label className="block text-xs text-white/50 mb-1">Welcome Message (Chat)</label>
          <textarea className="w-full input-glass min-h-[60px] text-sm" value={form.welcome_message} onChange={e => setForm({ ...form, welcome_message: e.target.value })} data-testid="ai-welcome-input" />
        </div>
      </div>

      {/* Sales Script — only when paid config visible */}
      {showPaidConfig && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Sales Script (System Prompt)</h3>
              <p className="text-xs text-white/40 mt-1">This controls how the paid AI behaves, what it recommends, and how it sells.</p>
            </div>
          </div>
          <textarea
            className="w-full input-glass min-h-[400px] text-sm font-mono leading-relaxed"
            value={form.sales_script}
            onChange={e => setForm({ ...form, sales_script: e.target.value })}
            data-testid="ai-sales-script"
          />
        </div>
      )}

      {/* Save */}
      <button onClick={handleSave} disabled={saving} className="btn-primary py-3 px-8 flex items-center gap-2" data-testid="ai-settings-save">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save AI Settings'}
      </button>
    </div>
  );
};
